import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  DefaultButton,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  Panel,
  PanelType,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Icon,
  IconButton,
  TextField,
  Pivot,
  PivotItem,
  Separator,
  Modal,
} from '@fluentui/react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import { useAuth } from '../Auth/AuthProvider';
import { cloudManagementRAGService } from '../../services/cloudManagementRAGService';
import { buildApiUrl } from '../../config/apiConfig';
import ReactMarkdown from 'react-markdown';

interface CloudDashboardProps {
  className?: string;
}

// Category definitions with icons and colors
const CATEGORIES = {
  Security: {
    icon: 'Shield',
    color: '#e74c3c',
    description: 'Security recommendations and threat mitigations'
  },
  Cost: {
    icon: 'Money',
    color: '#2ecc71',
    description: 'Cost optimization and savings opportunities'
  },
  OperationalExcellence: {
    icon: 'Settings',
    color: '#3498db',
    description: 'Operational improvements and best practices'
  },
  Performance: {
    icon: 'SpeedHigh',
    color: '#f39c12',
    description: 'Performance optimization recommendations'
  },
  Reliability: {
    icon: 'Repair',
    color: '#9b59b6',
    description: 'Reliability and availability improvements'
  },
  Sustainability: {
    icon: 'Leaf',
    color: '#27ae60',
    description: 'Sustainability and environmental impact'
  }
};

interface CloudDashboardProps {
  className?: string;
}

interface Recommendation {
  id: string;
  category: string;
  impact: string;
  title: string;
  description: string;
  solution?: string;
  resourceId: string;
  subscription: string;
  provider?: 'azure' | 'gcp';
  potentialSavings?: number;
  severity?: 'High' | 'Medium' | 'Low';
  quickFix?: boolean;
  estimatedDowntime?: string;
  complexity?: 'Low' | 'Medium' | 'High';
  impactedResources?: number;
  lastUpdated?: string;
  mitigationScript?: string;
  prerequisites?: string[];
  rollbackPlan?: string;
  riskLevel?: 'Low' | 'Medium' | 'High';
  // Enhanced resource information
  subscriptionId?: string;
  resourceGroupName?: string;
  resourceName?: string;
  resourceType?: string;
  location?: string;
  impactedResourcesList?: Array<{
    name: string;
    type: string;
    resourceGroup: string;
    location: string;
  }>;
  // Extended properties for detailed cost/security information
  extendedProperties?: Record<string, any>;
  resourceMetadata?: Record<string, any>;
}

interface CloudData {
  subscriptions: any[];
  recommendations: Recommendation[];
  lastUpdated: string;
  totalSubscriptions: number;
  totalRecommendations: number;
  potentialSavings: number;
  categorySummary: Record<string, number>;
  impactSummary: Record<string, number>;
  prioritySummary: Record<string, number>;
  executiveSummary: string;
  healthScore?: number;
}

interface ExecutiveSummary {
  id: string;
  summary: string;
  key_insights: string[];
  priority_recommendations: Array<{
    category: string;
    recommendation: string;
    impact: string;
    urgency: string;
  }>;
  cost_analysis: {
    total_potential_savings: number;
    high_impact_savings: number;
    quick_wins: number;
  };
  risk_assessment: {
    security_risks: number;
    compliance_issues: number;
    operational_risks: number;
  };
  generated_at: string;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: string;
  trend?: number;
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  trend,
  color = '#667eea'
}) => (
  <div style={{ 
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    padding: '24px',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    minWidth: '280px',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '140px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  }}>
    {/* Background decoration */}
    <div style={{
      position: 'absolute',
      top: '-20px',
      right: '-20px',
      width: '80px',
      height: '80px',
      background: `linear-gradient(135deg, ${color}20, ${color}10)`,
      borderRadius: '50%',
    }} />
    
    <div style={{ position: 'relative', zIndex: 1 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
        <Stack>
          <Text variant="medium" style={{ 
            color: '#4a5568', 
            fontWeight: '500',
            marginBottom: '8px'
          }}>
            {title}
          </Text>
          {icon && (
            <div style={{
              background: `linear-gradient(135deg, ${color}, ${color}dd)`,
              borderRadius: '12px',
              padding: '8px',
              display: 'inline-block',
              marginBottom: '12px'
            }}>
              <Icon 
                iconName={icon} 
                style={{ 
                  fontSize: '20px', 
                  color: '#ffffff'
                }} 
              />
            </div>
          )}
        </Stack>
        {trend !== undefined && (
          <div style={{
            background: trend >= 0 ? 'rgba(46, 204, 113, 0.2)' : 'rgba(231, 76, 60, 0.2)',
            padding: '4px 8px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Icon 
              iconName={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} 
              style={{ fontSize: '12px', color: trend >= 0 ? '#2ecc71' : '#e74c3c' }} 
            />
            <Text variant="small" style={{ color: trend >= 0 ? '#2ecc71' : '#e74c3c', fontWeight: '600' }}>
              {Math.abs(trend)}%
            </Text>
          </div>
        )}
      </Stack>
      
      <Text style={{ 
        fontWeight: '700', 
        margin: '8px 0',
        color: '#2d3748',
        fontSize: '28px',
        lineHeight: '1.2'
      }}>
        {value}
      </Text>
      
      {subtitle && (
        <Text variant="small" style={{ 
          color: '#718096',
          fontSize: '13px'
        }}>
          {subtitle}
        </Text>
      )}
    </div>
  </div>
);

const ChartWidget: React.FC<{
  title: string;
  children: React.ReactNode;
  icon?: string;
  height?: string;
}> = ({ title, children, icon, height = '400px' }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    minHeight: height,
  }}>
    <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
      {icon && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '16px',
          padding: '12px',
          marginRight: '16px'
        }}>
          <Icon iconName={icon} style={{ fontSize: '24px', color: '#ffffff' }} />
        </div>
      )}
      <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
        {title}
      </Text>
    </Stack>
    
    <div style={{ 
      height: `calc(${height} - 100px)`,
      position: 'relative'
    }}>
      {children}
    </div>
  </div>
);

// Category Widget Component for Azure Advisor-style layout
const CategoryWidget: React.FC<{
  title: string;
  icon: string;
  color: string;
  totalRecommendations: number;
  highImpact: number;
  mediumImpact: number;
  lowImpact: number;
  description: string;
  onClick: () => void;
}> = ({ title, icon, color, totalRecommendations, highImpact, mediumImpact, lowImpact, description, onClick }) => (
  <div 
    style={{
      background: 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minHeight: '180px'
    }}
    onClick={onClick}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)';
    }}
  >
  <Stack horizontal verticalAlign="center" style={{ marginBottom: '12px' }}>
      <div style={{
        background: color,
        borderRadius: '12px',
        padding: '12px',
        marginRight: '16px'
      }}>
        <Icon iconName={icon} style={{ fontSize: '24px', color: '#ffffff' }} />
      </div>
      <Stack>
        <Text variant="large" style={{ fontWeight: '600', color: '#2d3748' }}>
          {title}
        </Text>
        <Text variant="medium" style={{ color: '#718096' }}>
          {totalRecommendations} recommendation{totalRecommendations !== 1 ? 's' : ''}
        </Text>
      </Stack>
    </Stack>
    
  <Text variant="small" style={{ color: '#4a5568', marginBottom: '12px', lineHeight: '1.6' }}>
      {description}
    </Text>
    
  <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap', paddingTop: '10px' }}>
      {highImpact > 0 && (
        <div style={{
          background: '#e74c3c',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          minWidth: '50px',
          textAlign: 'center'
        }}>
          {highImpact} High
        </div>
      )}
      {mediumImpact > 0 && (
        <div style={{
          background: '#f39c12',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          minWidth: '50px',
          textAlign: 'center'
        }}>
          {mediumImpact} Medium
        </div>
      )}
      {lowImpact > 0 && (
        <div style={{
          background: '#2ecc71',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          minWidth: '50px',
          textAlign: 'center'
        }}>
          {lowImpact} Low
        </div>
      )}
    </div>
    
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: '12px',
      paddingTop: '10px'
    }}>
      <Text variant="small" style={{ color: '#667eea', fontWeight: '500' }}>
        View details →
      </Text>
    </div>
  </div>
);

// Recommendation Detail Modal Component
const RecommendationDetailModal: React.FC<{
  recommendation: Recommendation | null;
  isOpen: boolean;
  onClose: () => void;
  onGenerateScript: (rec: Recommendation) => Promise<string>;
  generatedScripts: {[key: string]: string};
  scriptGenerationLoading: {[key: string]: boolean};
}> = ({ recommendation, isOpen, onClose, onGenerateScript, generatedScripts, scriptGenerationLoading }) => {
  const [currentScript, setCurrentScript] = React.useState<string>('');
  const [isApplyingFix, setIsApplyingFix] = React.useState<boolean>(false);
  const [applyFixResult, setApplyFixResult] = React.useState<any>(null);
  
  if (!recommendation) return null;

  const scriptKey = recommendation.id;
  const hasGeneratedScript = !!generatedScripts[scriptKey];
  const isGeneratingScript = !!scriptGenerationLoading[scriptKey];

  const handleGenerateScript = async () => {
    const script = await onGenerateScript(recommendation);
    setCurrentScript(script);
  };

  const handleApplyFix = async () => {
    if (!hasGeneratedScript) {
      alert('Please generate the fix script first');
      return;
    }

    setIsApplyingFix(true);
    setApplyFixResult(null);

    try {
      const response = await fetch(buildApiUrl('/cloud/apply-fix'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recommendation_id: recommendation.id,
          script_content: generatedScripts[scriptKey],
          subscription_id: recommendation.subscriptionId,
          resource_group: recommendation.resourceGroupName,
          resource_name: recommendation.resourceName
        })
      });

      const result = await response.json();

      if (result.success) {
        setApplyFixResult({
          success: true,
          execution_id: result.execution_id,
          status: result.status,
          message: result.message,
          logs: result.logs,
          timestamp: result.timestamp
        });
      } else {
        setApplyFixResult({
          success: false,
          error: result.error
        });
      }
    } catch (error) {
      console.error('Error applying fix:', error);
      setApplyFixResult({
        success: false,
        error: 'Network error occurred while applying fix'
      });
    } finally {
      setIsApplyingFix(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onClose}
      isBlocking={false}
      containerClassName="recommendation-modal"
    >
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '600px',
        maxHeight: '90vh',
        width: '800px',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          padding: '32px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack tokens={{ childrenGap: 12 }}>
              <Text variant="xLarge" style={{ color: 'white', fontWeight: '600', marginBottom: '12px' }}>
                {recommendation.title}
              </Text>
              <Stack horizontal tokens={{ childrenGap: 16 }}>
                <div style={{
                  background: recommendation.impact === 'High' ? '#e74c3c' : 
                             recommendation.impact === 'Medium' ? '#f39c12' : '#2ecc71',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}>
                  {recommendation.impact} Impact
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  {recommendation.category}
                </div>
                {recommendation.quickFix && (
                  <div style={{
                    background: '#2ecc71',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    Quick Fix
                  </div>
                )}
              </Stack>
            </Stack>
            <IconButton
              iconProps={{ iconName: 'Cancel' }}
              onClick={onClose}
              styles={{
                root: {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  width: '44px',
                  height: '44px'
                }
              }}
            />
          </Stack>
        </div>

        {/* Content */}
        <div style={{
          padding: '32px',
          maxHeight: 'calc(90vh - 200px)',
          overflowY: 'auto',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)'
        }}>
          <Stack tokens={{ childrenGap: 32 }}>
            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <Stack horizontal verticalAlign="center" style={{ marginBottom: '12px' }}>
                <Icon iconName="Info" style={{ color: '#667eea', marginRight: '8px' }} />
                <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                  Description
                </Text>
              </Stack>
              <Text style={{ color: '#4a5568', lineHeight: '1.6', paddingLeft: '24px' }}>
                {recommendation.description}
              </Text>
            </div>

            {/* Solution (for Cost recommendations) */}
            {recommendation.solution && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '12px' }}>
                  <Icon iconName="Lightbulb" style={{ color: '#2ecc71', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Solution
                  </Text>
                </Stack>
                <div style={{
                  background: 'rgba(46, 204, 113, 0.05)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(46, 204, 113, 0.2)',
                  marginLeft: '24px'
                }}>
                  <Text style={{ color: '#4a5568', lineHeight: '1.6' }}>
                    {recommendation.solution}
                  </Text>
                </div>
              </div>
            )}

            {/* Cost Details (for Cost category recommendations) */}
            {recommendation.category === 'Cost' && recommendation.extendedProperties && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '16px' }}>
                  <Icon iconName="Money" style={{ color: '#2ecc71', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Cost Optimization Details
                  </Text>
                </Stack>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginLeft: '24px'
                }}>
                  {recommendation.extendedProperties.savingsAmount && (
                    <div style={{
                      background: 'rgba(46, 204, 113, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(46, 204, 113, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#2ecc71', marginBottom: '4px' }} styles={{ root: { display: 'block' } }}>
                        MONTHLY SAVINGS
                      </Text>
                      <Text variant="medium" style={{ color: '#2d3748', fontWeight: '600' }} styles={{ root: { display: 'block' } }}>
                        ${recommendation.extendedProperties.savingsAmount} {recommendation.extendedProperties.savingsCurrency || 'USD'}
                      </Text>
                    </div>
                  )}
                  {recommendation.extendedProperties.annualSavingsAmount && (
                    <div style={{
                      background: 'rgba(46, 204, 113, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(46, 204, 113, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#2ecc71', marginBottom: '4px' }} styles={{ root: { display: 'block' } }}>
                        ANNUAL SAVINGS
                      </Text>
                      <Text variant="medium" style={{ color: '#2d3748', fontWeight: '600' }} styles={{ root: { display: 'block' } }}>
                        ${recommendation.extendedProperties.annualSavingsAmount} {recommendation.extendedProperties.savingsCurrency || 'USD'}
                      </Text>
                    </div>
                  )}
                  {recommendation.extendedProperties.displaySKU && (
                    <div style={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }} styles={{ root: { display: 'block' } }}>
                        SKU
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                        {recommendation.extendedProperties.displaySKU}
                      </Text>
                    </div>
                  )}
                  {recommendation.extendedProperties.region && (
                    <div style={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }} styles={{ root: { display: 'block' } }}>
                        REGION
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                        {recommendation.extendedProperties.region}
                      </Text>
                    </div>
                  )}
                  {recommendation.extendedProperties.term && (
                    <div style={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }} styles={{ root: { display: 'block' } }}>
                        TERM
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                        {recommendation.extendedProperties.term}
                      </Text>
                    </div>
                  )}
                  {recommendation.extendedProperties.displayQty && (
                    <div style={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }} styles={{ root: { display: 'block' } }}>
                        QUANTITY
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                        {recommendation.extendedProperties.displayQty}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Security Details (for Security category recommendations) */}
            {recommendation.category === 'Security' && (recommendation.resourceMetadata || recommendation.extendedProperties) && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '16px' }}>
                  <Icon iconName="Shield" style={{ color: '#e74c3c', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Security Details
                  </Text>
                </Stack>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginLeft: '24px'
                }}>
                  {recommendation.resourceName && (
                    <div style={{
                      background: 'rgba(231, 76, 60, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(231, 76, 60, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#e74c3c', marginBottom: '4px' }}>
                        RESOURCE NAME
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748', wordBreak: 'break-all' }}>
                        {recommendation.resourceName}
                      </Text>
                    </div>
                  )}
                  {recommendation.resourceMetadata?.action && (
                    <div style={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }}>
                        ACTION
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748' }}>
                        {recommendation.resourceMetadata.action}
                      </Text>
                    </div>
                  )}
                  {recommendation.resourceMetadata?.source && (
                    <div style={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }}>
                        SOURCE
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748', wordBreak: 'break-all' }}>
                        {recommendation.resourceMetadata.source}
                      </Text>
                    </div>
                  )}
                  {recommendation.extendedProperties?.assessmentKey && (
                    <div style={{
                      background: 'rgba(231, 76, 60, 0.1)',
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(231, 76, 60, 0.2)',
                      textAlign: 'center'
                    }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#e74c3c', marginBottom: '4px' }}>
                        ASSESSMENT KEY
                      </Text>
                      <Text variant="small" style={{ color: '#2d3748', wordBreak: 'break-all' }}>
                        {recommendation.extendedProperties.assessmentKey}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Impacted Resources */}
            {recommendation.impactedResourcesList && recommendation.impactedResourcesList.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '16px' }}>
                  <Icon iconName="ServerEnviroment" style={{ color: '#667eea', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Impacted Resources ({recommendation.impactedResourcesList.length})
                  </Text>
                </Stack>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.05)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  marginLeft: '24px'
                }}>
                  {recommendation.impactedResourcesList.map((resource, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: index < recommendation.impactedResourcesList!.length - 1 ? '1px solid rgba(102, 126, 234, 0.1)' : 'none'
                    }}>
                      <div>
                        <Text variant="small" style={{ fontWeight: '600', color: '#2d3748', marginBottom: '4px' }}>
                          {resource.name}
                        </Text>
                        <Text variant="xSmall" style={{ color: '#4a5568', display: 'block' }}>
                          {resource.type} | {resource.resourceGroup} | {resource.location}
                        </Text>
                      </div>
                      <Icon iconName="ChevronRight" style={{ color: '#667eea', fontSize: '14px' }} />
                    </div>
                  ))}
                </div>
                
                {/* Resource Summary */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                  marginTop: '16px',
                  marginLeft: '24px'
                }}>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(102, 126, 234, 0.1)', borderRadius: '8px' }}>
                    <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }}>
                      {(recommendation as any).provider === 'gcp' ? 'PROJECT' : 'SUBSCRIPTION'}
                    </Text>
                    <Text variant="xSmall" style={{ color: '#4a5568', display: 'block' }}>{recommendation.subscriptionId?.substring(0, 8)}...</Text>
                  </div>
                  {((recommendation as any).provider !== 'gcp') && (
                    <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(102, 126, 234, 0.1)', borderRadius: '8px' }}>
                      <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }}>RESOURCE GROUP</Text>
                      <Text variant="xSmall" style={{ color: '#4a5568', display: 'block' }}>{recommendation.resourceGroupName}</Text>
                    </div>
                  )}
                  <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(102, 126, 234, 0.1)', borderRadius: '8px' }}>
                    <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '4px' }}>LOCATION</Text>
                    <Text variant="xSmall" style={{ color: '#4a5568', display: 'block' }}>{recommendation.location}</Text>
                  </div>
                </div>
              </div>
            )}

            {/* Impact Details */}
            <div style={{ marginBottom: '24px' }}>
              <Stack horizontal verticalAlign="center" style={{ marginBottom: '16px' }}>
                <Icon iconName="ReportHacked" style={{ color: '#667eea', marginRight: '8px' }} />
                <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                  Impact Assessment
                </Text>
              </Stack>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
                marginLeft: '24px'
              }}>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  textAlign: 'center'
                }}>
                  <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '8px' }} styles={{ root: { display: 'block' } }}>
                    ESTIMATED DOWNTIME
                  </Text>
                  <Text style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                    {recommendation.estimatedDowntime || 'None expected'}
                  </Text>
                </div>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  textAlign: 'center'
                }}>
                  <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '8px' }} styles={{ root: { display: 'block' } }}>
                    COMPLEXITY
                  </Text>
                  <Text style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                    {recommendation.complexity || 'Medium'}
                  </Text>
                </div>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  textAlign: 'center'
                }}>
                  <Text variant="small" style={{ fontWeight: '600', color: '#667eea', marginBottom: '8px' }} styles={{ root: { display: 'block' } }}>
                    RISK LEVEL
                  </Text>
                  <Text style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                    {recommendation.riskLevel || 'Low'}
                  </Text>
                </div>
              </div>
            </div>

            {/* Mitigation Script Section */}
            <div style={{ marginBottom: '24px' }}>
              <Stack horizontal verticalAlign="center" horizontalAlign="space-between" style={{ marginBottom: '16px' }}>
                <Stack horizontal verticalAlign="center">
                  <Icon iconName="Code" style={{ color: '#667eea', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Mitigation Script
                  </Text>
                </Stack>
                
                {!hasGeneratedScript && (
                  <PrimaryButton
                    text={isGeneratingScript ? "Generating..." : "Generate Fix Script"}
                    iconProps={{ iconName: isGeneratingScript ? 'Sync' : 'Code' }}
                    onClick={handleGenerateScript}
                    disabled={isGeneratingScript}
                    styles={{
                      root: {
                        backgroundColor: '#667eea',
                        border: 'none',
                        borderRadius: '8px',
                        minWidth: '160px',
                        height: '36px'
                      }
                    }}
                  />
                )}
              </Stack>
              
              <div style={{ marginLeft: '24px' }}>
                {hasGeneratedScript && (
                  <div style={{
                    background: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '20px',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {generatedScripts[scriptKey]}
                    </pre>
                  </div>
                )}
                
                {!hasGeneratedScript && !isGeneratingScript && (
                  <div style={{
                    background: 'rgba(102, 126, 234, 0.05)',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '2px dashed rgba(102, 126, 234, 0.2)',
                    textAlign: 'center'
                  }}>
                    <Icon iconName="Code" style={{ fontSize: '36px', color: '#667eea', marginBottom: '12px' }} />
                    <Text variant="medium" style={{ color: '#4a5568', marginBottom: '8px', display: 'block' }}>
                      {((recommendation as any).provider === 'gcp')
                        ? 'Click "Generate Fix Script" to create a gcloud CLI script'
                        : 'Click "Generate Fix Script" to create a PowerShell script'}
                    </Text>
                    <Text variant="small" style={{ color: '#718096' }}>
                      Customized for your specific resources and environment
                    </Text>
                  </div>
                )}
                
                {isGeneratingScript && (
                  <div style={{
                    background: 'rgba(102, 126, 234, 0.05)',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    textAlign: 'center'
                  }}>
                    <Spinner size={SpinnerSize.medium} />
                    <Text variant="medium" style={{ color: '#4a5568', marginTop: '16px' }}>
                      Generating customized fix script...
                    </Text>
                  </div>
                )}
              </div>
            </div>

            {/* Prerequisites */}
            {recommendation.prerequisites && recommendation.prerequisites.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '12px' }}>
                  <Icon iconName="CheckList" style={{ color: '#667eea', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Prerequisites
                  </Text>
                </Stack>
                <ul style={{ margin: 0, paddingLeft: '44px', color: '#4a5568', lineHeight: '1.6' }}>
                  {recommendation.prerequisites.map((prereq, index) => (
                    <li key={index} style={{ marginBottom: '8px' }}>{prereq}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rollback Plan */}
            {recommendation.rollbackPlan && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '12px' }}>
                  <Icon iconName="Undo" style={{ color: '#667eea', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Rollback Plan
                  </Text>
                </Stack>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.05)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  marginLeft: '24px'
                }}>
                  <Text style={{ color: '#4a5568', lineHeight: '1.6' }}>
                    {recommendation.rollbackPlan}
                  </Text>
                </div>
              </div>
            )}

            {/* Apply Fix Result */}
            {applyFixResult && (
              <div style={{ marginBottom: '24px' }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '16px' }}>
                  <Icon iconName={applyFixResult.success ? 'CheckMark' : 'ErrorBadge'} 
                        style={{ color: applyFixResult.success ? '#2ecc71' : '#e74c3c', marginRight: '8px' }} />
                  <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748' }}>
                    Execution Result
                  </Text>
                </Stack>
                <div style={{
                  background: applyFixResult.success ? 'rgba(46, 204, 113, 0.05)' : 'rgba(231, 76, 60, 0.05)',
                  border: `1px solid ${applyFixResult.success ? 'rgba(46, 204, 113, 0.2)' : 'rgba(231, 76, 60, 0.2)'}`,
                  borderRadius: '12px',
                  padding: '20px',
                  marginLeft: '24px'
                }}>
                  {applyFixResult.success ? (
                    <>
                      <Text variant="medium" style={{ fontWeight: '600', color: '#2ecc71', marginBottom: '8px' }}>
                        {applyFixResult.status} - {applyFixResult.message}
                      </Text>
                      <Text variant="small" style={{ color: '#4a5568', marginBottom: '12px' }}>
                        Execution ID: {applyFixResult.execution_id}
                      </Text>
                      {applyFixResult.logs && applyFixResult.logs.length > 0 && (
                        <div style={{
                          background: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          borderRadius: '8px',
                          padding: '12px',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '12px',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {applyFixResult.logs.map((log: string, index: number) => (
                            <div key={index} style={{ marginBottom: '4px' }}>
                              {log}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Text variant="medium" style={{ fontWeight: '600', color: '#e74c3c' }}>
                      Error: {applyFixResult.error}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(102, 126, 234, 0.2)' }}>
              <Stack horizontal tokens={{ childrenGap: 16 }}>
                <PrimaryButton
                  text={isApplyingFix ? "Applying Fix..." : "Apply Fix"}
                  iconProps={{ iconName: isApplyingFix ? 'Sync' : 'Play' }}
                  onClick={handleApplyFix}
                  disabled={isApplyingFix || !hasGeneratedScript}
                  styles={{
                    root: {
                      backgroundColor: hasGeneratedScript ? '#667eea' : '#cccccc',
                      border: 'none',
                      borderRadius: '8px',
                      height: '40px',
                      minWidth: '120px'
                    }
                  }}
                />
                <DefaultButton
                  text="Copy Script"
                  iconProps={{ iconName: 'Copy' }}
                  onClick={() => {
                    const scriptKey = recommendation.id;
                    if (generatedScripts[scriptKey]) {
                      navigator.clipboard.writeText(generatedScripts[scriptKey]);
                    }
                  }}
                  styles={{
                    root: {
                      borderRadius: '8px',
                      height: '40px',
                      minWidth: '120px'
                    }
                  }}
                />
                <DefaultButton
                  text="Download"
                  iconProps={{ iconName: 'Download' }}
                  styles={{
                    root: {
                      borderRadius: '8px',
                      height: '40px',
                      minWidth: '120px'
                    }
                  }}
                />
              </Stack>
            </div>
          </Stack>
        </div>
      </div>
    </Modal>
  );
};

export const CloudDashboard: React.FC<CloudDashboardProps> = ({ className }) => {
  const { getAccessToken, isAuthenticated, isLoading: authLoading } = useAuth();
  const [cloudData, setCloudData] = useState<CloudData>({
    subscriptions: [],
    recommendations: [],
    lastUpdated: '',
    totalSubscriptions: 0,
    totalRecommendations: 0,
    potentialSavings: 0,
    categorySummary: {},
    impactSummary: {},
    prioritySummary: {},
    executiveSummary: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPivot, setSelectedPivot] = useState<string>('dashboard');
  const [comprehensiveSummary, setComprehensiveSummary] = useState<string>('');
  const [loadingComprehensive, setLoadingComprehensive] = useState<boolean>(false);
  const [regenBusy, setRegenBusy] = useState<boolean>(false);
  type ChatMessage = { role: 'user' | 'assistant'; content: string; sources?: Array<any> };
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<ChatMessage>>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState<ExecutiveSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  
  // New state for category management and detailed views
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCategoryDetails, setShowCategoryDetails] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
  const [showFixDetails, setShowFixDetails] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isMitigationPanelOpen, setIsMitigationPanelOpen] = useState(false);
  const [selectedMitigationPhase, setSelectedMitigationPhase] = useState<string>('phase-1');
  const [aiMitigationResponse, setAiMitigationResponse] = useState<string>('');
  const [mitigationData, setMitigationData] = useState<{[key: string]: any}>({});
  const [mitigationLoading, setMitigationLoading] = useState(false);
  const [aiAssistantQuery, setAiAssistantQuery] = useState('');
  const [aiAssistantLoading, setAiAssistantLoading] = useState(false);
  const [generatedScripts, setGeneratedScripts] = useState<{[key: string]: string}>({});
  const [scriptGenerationLoading, setScriptGenerationLoading] = useState<{[key: string]: boolean}>({});
  const [selectedProvider, setSelectedProvider] = useState<'all' | 'azure' | 'gcp'>('all');

  // Load backend-generated comprehensive summary when tab opens or provider changes
  useEffect(() => {
    const load = async () => {
      if (selectedPivot !== 'comprehensive-summary') return;
      setLoadingComprehensive(true);
      try {
        const prov = selectedProvider === 'all' ? undefined : selectedProvider;
        const res = await cloudManagementRAGService.getComprehensiveSummary(prov as any);
        setComprehensiveSummary(res.summary || '');
      } catch (e) {
        // Fallback to local builder
        setComprehensiveSummary(buildComprehensiveSummary(cloudData.recommendations as any, selectedProvider));
      } finally {
        setLoadingComprehensive(false);
      }
    };
    load();
  }, [selectedPivot, selectedProvider, cloudData.recommendations]);

  const handleRegenerateComprehensive = async () => {
    try {
      setRegenBusy(true);
      const prov = selectedProvider === 'all' ? 'azure' : selectedProvider; // default to azure if all
      await cloudManagementRAGService.regenerateComprehensiveSummary(prov as any);
      const res = await cloudManagementRAGService.getComprehensiveSummary(prov as any);
      setComprehensiveSummary(res.summary || '');
    } catch (e) {
      // no-op; keep current content
    } finally {
      setRegenBusy(false);
    }
  };

  // Executive Summary CSS (scoped)
  const execSummaryStyles = `
    .exec-summary { line-height: 1.6; color: #4a5568; }
    .exec-summary .exec-h1 { font-size: 32px; font-weight: 800; color: #2d3748; margin: 0 0 16px; }
    .exec-summary .exec-h2 { font-size: 24px; font-weight: 700; color: #2d3748; margin: 24px 0 12px; }
    .exec-summary .exec-h3 { font-size: 18px; font-weight: 700; color: #4a5568; margin: 16px 0 8px; }
    .exec-summary .exec-h4 { font-size: 16px; font-weight: 700; color: #4a5568; margin: 12px 0 8px; }
    .exec-summary .exec-p  { margin: 0 0 12px; }
    .exec-summary ul, .exec-summary ol { margin: 0 0 12px 20px; }
    /* Layout rule: for the first h2 (main title), show the next two elements on one row, then the following element below */
    .exec-summary h2.exec-title + * { display: inline-block; width: calc(50% - 8px); vertical-align: top; margin-right: 16px; }
    .exec-summary h2.exec-title + * + * { display: inline-block; width: calc(50% - 8px); vertical-align: top; margin-right: 0; }
    .exec-summary h2.exec-title + * + * + * { display: block; width: 100%; clear: both; }
  `;

  // Normalize impact to High/Medium/Low for UI across providers
  const normalizeImpact = (rec: Recommendation): 'High' | 'Medium' | 'Low' => {
    const provider = (rec as any).provider;
    const impactRaw = (rec.impact || '').toString();
    const priorityRaw = ((rec as any).priority || '').toString();
    if (provider === 'gcp') {
      const pr = priorityRaw.toUpperCase();
      if (pr.includes('P1') || pr.includes('HIGH')) return 'High';
      if (pr.includes('P2') || pr.includes('MEDIUM')) return 'Medium';
      if (pr.includes('P3') || pr.includes('LOW')) return 'Low';
      if (pr.includes('P4')) return 'Low';
      // Fallback when no priority mapping is available
      return 'Medium';
    }
    const i = impactRaw.toLowerCase();
    if (i === 'high') return 'High';
    if (i === 'medium') return 'Medium';
    if (i === 'low') return 'Low';
    return 'Medium';
  };

  // Normalize category to our fixed set for UI across providers
  const providerCategories = new Set(Object.keys(CATEGORIES).map(k => k.toLowerCase()));
  const normalizeCategory = (rec: Recommendation): string => {
    const provider = (rec as any).provider;
    const rawCat = (rec.category || '').toString();
    const rawImp = (rec.impact || '').toString();

    // If already a known UI category, keep it
    if (providerCategories.has(rawCat.toLowerCase())) return rawCat;

    // For GCP, prefer primary impact categories (Cost, Security, Performance, Reliability)
    if (provider === 'gcp') {
      const impLc = rawImp.toLowerCase();
      if (['cost', 'security', 'performance', 'reliability'].includes(impLc)) {
        // Title-case to match our UI labels
        return impLc.charAt(0).toUpperCase() + impLc.slice(1);
      }
      const lc = rawCat.toLowerCase();
      if (lc.includes('idle') || lc.includes('machine') || lc.includes('compute') || lc.includes('disk') || lc.includes('cost')) return 'Cost';
      if (lc.includes('iam') || lc.includes('policy') || lc.includes('security') || lc.includes('kms') || lc.includes('firewall')) return 'Security';
      if (lc.includes('performance')) return 'Performance';
      if (lc.includes('reliab') || lc.includes('availability')) return 'Reliability';
      // Default to Performance for unknown GCP subtypes rather than Azure-specific terms
      return 'Performance';
    }

    // Non-GCP: try to coerce via impact as category
    if (providerCategories.has(rawImp.toLowerCase())) return rawImp;

    const lc = rawCat.toLowerCase();
    if (lc.includes('cost')) return 'Cost';
    if (lc.includes('security')) return 'Security';
    if (lc.includes('performance')) return 'Performance';
    if (lc.includes('reliab') || lc.includes('availability')) return 'Reliability';
    return 'OperationalExcellence';
  };

  // Helper functions for category management
  const getCategoryStats = (category: string) => {
    const categoryRecs = cloudData.recommendations.filter(rec => normalizeCategory(rec) === category);
    return {
      total: categoryRecs.length,
      high: categoryRecs.filter(rec => normalizeImpact(rec) === 'High').length,
      medium: categoryRecs.filter(rec => normalizeImpact(rec) === 'Medium').length,
      low: categoryRecs.filter(rec => normalizeImpact(rec) === 'Low').length,
    };
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setShowCategoryDetails(true);
  };

  const handleRecommendationClick = (recommendation: Recommendation) => {
    setSelectedRecommendation(recommendation);
    setShowFixDetails(true);
  };

  // Function to generate mitigation script on-demand
  const generateMitigationScript = async (recommendation: Recommendation) => {
    const scriptKey = recommendation.id;
    
    // Check if script is already generated
    if (generatedScripts[scriptKey]) {
      return generatedScripts[scriptKey];
    }
    
    // Set loading state for this specific recommendation
    setScriptGenerationLoading(prev => ({ ...prev, [scriptKey]: true }));
    
    try {
      // Find the enhanced resource info for this recommendation
      const enhancedRec = cloudData.recommendations.find(r => r.id === recommendation.id);
      const enhancedResourceInfo = enhancedRec ? {
        subscriptionId: enhancedRec.subscriptionId,
        resourceGroupName: enhancedRec.resourceGroupName,
        resourceName: enhancedRec.resourceName,
        resourceType: enhancedRec.resourceType,
        location: enhancedRec.location,
        impactedResourcesList: enhancedRec.impactedResourcesList
      } : null;
      
      // Generate the script
      const script = getMitigationScript(recommendation, enhancedResourceInfo);
      
      // Store the generated script
      setGeneratedScripts(prev => ({ ...prev, [scriptKey]: script }));
      
      return script;
    } catch (error) {
      console.error('Error generating mitigation script:', error);
      return 'Error generating script. Please try again.';
    } finally {
      setScriptGenerationLoading(prev => ({ ...prev, [scriptKey]: false }));
    }
  };

  const loadCloudData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
  const providerParam = selectedProvider !== 'all' ? `?provider=${selectedProvider}` : '';
  const response = await fetch(buildApiUrl(`/cloud/dashboard${providerParam}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load cloud data');
      }

      const result = await response.json();
      // Backend returns data in result.data, not directly
      const data = result.data || {};
      
      // Ensure recommendations array exists
      if (!data.recommendations) {
        data.recommendations = [];
      }
      
      // Enhance recommendations with detailed fix information
      if (data.recommendations.length > 0) {
        data.recommendations = enhanceRecommendations(data.recommendations);
      }
      
      setCloudData(data);
    } catch (error) {
      setError(`Error loading cloud data: ${error}`);
      console.error('Error loading cloud data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExecutiveSummary = async () => {
    try {
      setSummaryLoading(true);
      
      const token = await getAccessToken();
  // Append provider filter if not 'all'
  const providerParam = selectedProvider !== 'all' ? `?provider=${selectedProvider}` : '';
  const response = await fetch(buildApiUrl(`/cloud/executive-summary${providerParam}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load executive summary');
      }

      const result = await response.json();
      
      if (result.success && result.summary) {
        // Backend returns the summary fields directly in the response
        const summaryData = {
          id: result.id || result.summary,
          summary: result.summary,
          key_insights: result.key_insights || [],
          priority_recommendations: result.priority_recommendations || [],
          cost_analysis: result.cost_analysis || {
            total_potential_savings: 0,
            high_impact_savings: 0,
            quick_wins: 0
          },
          risk_assessment: result.risk_assessment || {
            security_risks: 0,
            compliance_issues: 0,
            operational_risks: 0
          },
          generated_at: result.timestamp || result.date,
          statistics: result.statistics || {}
        };
        setExecutiveSummary(summaryData);
      } else {
        // Handle case where the RAG service returned the mock data structure directly
        setExecutiveSummary(result);
      }
    } catch (error) {
      console.error('Error loading executive summary:', error);
      setExecutiveSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Function to load mitigation data for a specific phase
  const loadMitigationData = async (phase: string) => {
    setMitigationLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(buildApiUrl(`/api/admin/cloud-management/mitigation/${phase}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMitigationData(prev => ({
          ...prev,
          [phase]: data
        }));
      } else {
        console.error('Failed to load mitigation data:', response.status);
        setMitigationData(prev => ({
          ...prev,
          [phase]: { error: 'Failed to load data' }
        }));
      }
    } catch (error) {
      console.error('Error loading mitigation data:', error);
      setMitigationData(prev => ({
        ...prev,
        [phase]: { error: 'Network error' }
      }));
    } finally {
      setMitigationLoading(false);
    }
  };

  // Function to handle AI assistant queries
  const handleAiAssistantQuery = async () => {
    if (!aiAssistantQuery.trim()) return;
    
    setAiAssistantLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(buildApiUrl('/api/admin/cloud-management/ai-assistant'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: aiAssistantQuery,
          phase: selectedMitigationPhase
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiMitigationResponse(data.response);
      } else {
        setAiMitigationResponse('Sorry, I encountered an error processing your request.');
      }
    } catch (error) {
      console.error('Error querying AI assistant:', error);
      setAiMitigationResponse('Network error. Please try again.');
    } finally {
      setAiAssistantLoading(false);
    }
  };

  // Function to generate realistic resource information
  const getEnhancedResourceInfo = (rec: Recommendation, index: number) => {
    const isGcp = (rec as any).provider === 'gcp';
    const subscriptionId = (rec as any).subscriptionId || "8f897d1f-11ce-4e3a-8b0d-7cbc130976f5"; // Azure sub or GCP project ID
    const locationsAzure = ['East US', 'West Europe', 'Southeast Asia', 'Central US', 'UK South'];
    const gcpZone = (rec as any).resourceMetadata?.zone;
    const gcpRegion = (rec as any).resourceMetadata?.region;
    const location = isGcp ? (gcpZone || gcpRegion || 'us-central1') : locationsAzure[index % locationsAzure.length];
    
    // Generate resource names based on category and type
    const fallbackName = isGcp ? `gce-vm-${String(index + 1).padStart(3, '0')}` : `vm-prod-${String(index + 1).padStart(3, '0')}`;
    const resourceId = (rec as any).resourceId || (rec as any).resourceMetadata?.resourceId;
    let resourceName = (rec as any).resourceName || (typeof resourceId === 'string' && resourceId.includes('/') ? resourceId.split('/').pop() : undefined) || fallbackName;
    let resourceType = '';
    let resourceGroupName = isGcp ? '-' : '';
    let impactedResourcesList: Array<{name: string, type: string, resourceGroup: string, location: string}> = [];
    
    if (rec.category === 'Cost') {
      if (rec.title.includes('virtual machine')) {
        if (isGcp) {
          resourceType = 'compute.googleapis.com/Instance';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `vm-prod-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.Compute/virtualMachines';
          resourceGroupName = `rg-production-vms`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location },
            { name: `${resourceName}-osdisk`, type: 'Microsoft.Compute/disks', resourceGroup: resourceGroupName, location },
            { name: `${resourceName}-nic`, type: 'Microsoft.Network/networkInterfaces', resourceGroup: resourceGroupName, location }
          ];
        }
      } else if (rec.title.includes('storage')) {
        if (isGcp) {
          resourceName = `pd-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'compute.googleapis.com/Disk';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `stprod${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.Storage/storageAccounts';
          resourceGroupName = `rg-production-storage`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location }
          ];
        }
      } else {
        if (isGcp) {
          resourceName = `gce-cost-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'compute.googleapis.com/Instance';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `res-cost-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.Compute/virtualMachines';
          resourceGroupName = `rg-cost-optimization`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location }
          ];
        }
      }
    } else if (rec.category === 'Security') {
      if (rec.title.includes('Key Vault') || rec.title.includes('vault')) {
        if (isGcp) {
          resourceName = `kms-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'cloudkms.googleapis.com/KeyRing';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `kv-prod-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.KeyVault/vaults';
          resourceGroupName = `rg-security-keyvaults`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location }
          ];
        }
      } else if (rec.title.includes('network') || rec.title.includes('NSG')) {
        if (isGcp) {
          resourceName = `fw-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'compute.googleapis.com/Firewall';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `nsg-prod-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.Network/networkSecurityGroups';
          resourceGroupName = `rg-network-security`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location },
            { name: `vnet-prod-${String(index + 1).padStart(3, '0')}`, type: 'Microsoft.Network/virtualNetworks', resourceGroup: resourceGroupName, location }
          ];
        }
      } else if (rec.title.includes('storage')) {
        if (isGcp) {
          resourceName = `bucket-sec-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'storage.googleapis.com/Bucket';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `stsec${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.Storage/storageAccounts';
          resourceGroupName = `rg-security-storage`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location }
          ];
        }
      } else {
        if (isGcp) {
          resourceName = `gce-sec-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'compute.googleapis.com/Instance';
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: '-', location }
          ];
        } else {
          resourceName = `vm-sec-${String(index + 1).padStart(3, '0')}`;
          resourceType = 'Microsoft.Compute/virtualMachines';
          resourceGroupName = `rg-security-vms`;
          impactedResourcesList = [
            { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location },
            { name: `${resourceName}-osdisk`, type: 'Microsoft.Compute/disks', resourceGroup: resourceGroupName, location }
          ];
        }
      }
    } else { // OperationalExcellence
      if (isGcp) {
        resourceName = `gce-ops-${String(index + 1).padStart(3, '0')}`;
        resourceType = 'compute.googleapis.com/Instance';
        impactedResourcesList = [
          { name: resourceName, type: resourceType, resourceGroup: '-', location }
        ];
      } else {
        resourceName = `vm-ops-${String(index + 1).padStart(3, '0')}`;
        resourceType = 'Microsoft.Compute/virtualMachines';
        resourceGroupName = `rg-operational-excellence`;
        impactedResourcesList = [
          { name: resourceName, type: resourceType, resourceGroup: resourceGroupName, location }
        ];
      }
    }
    
    return {
      subscriptionId,
  resourceGroupName,
      resourceName,
      resourceType,
      location,
      impactedResourcesList
    };
  };

  // Function to enhance recommendations with detailed fix information
  const enhanceRecommendations = (recommendations: Recommendation[]): Recommendation[] => {
    return recommendations.map((rec, index) => {
      const enhancedResourceInfo = getEnhancedResourceInfo(rec, index);
      return {
        ...rec,
        ...enhancedResourceInfo,
  // Keep provider for downstream logic (script generation, labels)
  provider: (rec as any).provider as any,
  // Normalize impact severity across providers
  severity: normalizeImpact(rec),
  // Quick fix heuristic: cost + high impact after normalization
  quickFix: normalizeCategory(rec) === 'Cost' && normalizeImpact(rec) === 'High',
        estimatedDowntime: getEstimatedDowntime(rec),
        complexity: getComplexity(rec),
        impactedResources: enhancedResourceInfo.impactedResourcesList.length,
        // Don't generate script automatically - only on demand
        prerequisites: getPrerequisites(rec),
        rollbackPlan: getRollbackPlan(rec),
        riskLevel: getRiskLevel(rec),
      };
    });
  };

  const getEstimatedDowntime = (rec: Recommendation): string => {
    if (rec.category === 'Security') return '< 5 minutes';
    if (rec.category === 'Cost') return 'None expected';
    if (rec.impact === 'High') return '15-30 minutes';
    return '< 10 minutes';
  };

  const getComplexity = (rec: Recommendation): 'Low' | 'Medium' | 'High' => {
    if (rec.category === 'Cost') return 'Low';
    if (rec.impact === 'High') return 'Medium';
    return 'Low';
  };

  const getRiskLevel = (rec: Recommendation): 'Low' | 'Medium' | 'High' => {
    if (rec.category === 'Security') return 'High';
    if (rec.impact === 'High') return 'Medium';
    return 'Low';
  };

  const getMitigationScript = (rec: Recommendation, enhancedResourceInfo?: any): string => {
    // Prefer backend-provided fields (subscriptionId may be Azure sub or GCP project)
    const subscriptionId = (rec as any).subscriptionId || enhancedResourceInfo?.subscriptionId || "8f897d1f-11ce-4e3a-8b0d-7cbc130976f5";
    const resourceGroupName = (rec as any).resourceGroupName || enhancedResourceInfo?.resourceGroupName || "rg-unknown";
    const resourceName = enhancedResourceInfo?.resourceName || (rec as any).resourceName || "resource-unknown";
    const resourceType = enhancedResourceInfo?.resourceType || (rec as any).resourceType || "Unknown";
    const location = enhancedResourceInfo?.location || (rec as any).resourceMetadata?.zone || (rec as any).resourceMetadata?.region || "us-central1";
    const impactedResources = enhancedResourceInfo?.impactedResourcesList || [];

    // If provider is Google Cloud, return gcloud-based scripts (Cost/Security/Default)
    if ((rec as any).provider === 'gcp') {
      const header = `# Google Cloud ${normalizeCategory(rec)} Script\n# Generated for: ${rec.title}\n# Project: ${subscriptionId}\n# Resource: ${resourceName}\n\n`;
      const auth = `gcloud auth login\ngcloud config set project ${subscriptionId}\n\n`;
      if (normalizeCategory(rec) === 'Cost') {
        return `${header}${auth}# Example: Rightsize Compute Engine instance\n` +
`gcloud compute instances stop ${resourceName} --zone=${location}\n` +
`gcloud compute instances set-machine-type ${resourceName} --machine-type=e2-standard-2 --zone=${location}\n` +
`gcloud compute instances start ${resourceName} --zone=${location}\n`;
      }
      if (normalizeCategory(rec) === 'Security') {
        return `${header}${auth}# Example: Restrict SSH/RDP from Internet\n` +
`gcloud compute firewall-rules create deny-rdp --direction=INGRESS --priority=1000 --action=DENY --rules=tcp:3389 --source-ranges=0.0.0.0/0 --network=default || true\n` +
`gcloud compute firewall-rules create deny-ssh --direction=INGRESS --priority=1001 --action=DENY --rules=tcp:22 --source-ranges=0.0.0.0/0 --network=default || true\n`;
      }
      // Default example for other categories
      return `${header}${auth}# Example: Tag a resource for tracking\n` +
`gcloud compute instances add-labels ${resourceName} --labels=owner=cloud-admin,env=prod --zone=${location}\n`;
    }

  if (rec.category === 'Cost') {
      return `# Azure Cost Optimization Script
# Generated for: ${rec.title}
# Resource: ${resourceName}
# Resource Group: ${resourceGroupName}
# Subscription: ${subscriptionId}
# Location: ${location}

# Connect to Azure
Connect-AzAccount

# Set subscription context
Set-AzContext -SubscriptionId "${subscriptionId}"

# Impacted Resources:
${impactedResources.map(res => `# - ${res.name} (${res.type}) in ${res.resourceGroup}`).join('\n')}

# Apply cost optimization
if ("${resourceType}" -eq "Microsoft.Compute/virtualMachines") {
    # Get VM details
    $vm = Get-AzVM -ResourceGroupName "${resourceGroupName}" -Name "${resourceName}"
    Write-Output "Current VM Size: $($vm.HardwareProfile.VmSize)"
    
    # Resize VM to optimized size based on current usage
    $optimizedSize = "Standard_B2s"  # Cost-optimized size
    $vm.HardwareProfile.VmSize = $optimizedSize
    
    # Stop VM before resizing
    Write-Output "Stopping VM ${resourceName}..."
    Stop-AzVM -ResourceGroupName "${resourceGroupName}" -Name "${resourceName}" -Force
    
    # Apply resize
    Write-Output "Resizing VM to $optimizedSize..."
    Update-AzVM -ResourceGroupName "${resourceGroupName}" -VM $vm
    
    # Start VM
    Write-Output "Starting VM ${resourceName}..."
    Start-AzVM -ResourceGroupName "${resourceGroupName}" -Name "${resourceName}"
    
    Write-Output "VM resized successfully. Estimated monthly savings: $${rec.potentialSavings || 50}"
    
} elseif ("${resourceType}" -eq "Microsoft.Storage/storageAccounts") {
    # Optimize storage account
    Write-Output "Optimizing storage account ${resourceName}..."
    
    # Change access tier to Cool for cost savings
    Set-AzStorageAccount -ResourceGroupName "${resourceGroupName}" -Name "${resourceName}" -AccessTier Cool
    
    # Enable lifecycle management
    $lifecyclePolicy = @{
        rules = @(
            @{
                name = "MoveToArchive"
                type = "Lifecycle"
                definition = @{
                    filters = @{
                        blobTypes = @("blockBlob")
                    }
                    actions = @{
                        baseBlob = @{
                            tierToArchive = @{
                                daysAfterModificationGreaterThan = 90
                            }
                            delete = @{
                                daysAfterModificationGreaterThan = 365
                            }
                        }
                    }
                }
            }
        )
    }
    
    Set-AzStorageAccountManagementPolicy -ResourceGroupName "${resourceGroupName}" -Name "${resourceName}" -Policy $lifecyclePolicy
    Write-Output "Storage optimization completed. Estimated monthly savings: $${rec.potentialSavings || 25}"
}

Write-Output "Cost optimization script completed successfully"`;
    }
    
  if (rec.category === 'Security') {
      return `# Azure Security Hardening Script
# Generated for: ${rec.title}
# Resource: ${resourceName}
# Resource Group: ${resourceGroupName}
# Subscription: ${subscriptionId}
# Location: ${location}

# Connect to Azure
Connect-AzAccount

# Set subscription context
Set-AzContext -SubscriptionId "${subscriptionId}"

# Impacted Resources:
${impactedResources.map(res => `# - ${res.name} (${res.type}) in ${res.resourceGroup}`).join('\n')}

# Apply security hardening based on recommendation type
if ("${rec.title}".Contains("encryption") -or "${rec.title}".Contains("Encryption")) {
    # Enable disk encryption for VMs
    if ("${resourceType}" -eq "Microsoft.Compute/virtualMachines") {
        Write-Output "Enabling disk encryption for VM ${resourceName}..."
        
        # Check if Key Vault exists, create if needed
        $keyVaultName = "kv-security-${resourceGroupName}"
        $keyVault = Get-AzKeyVault -ResourceGroupName "${resourceGroupName}" -VaultName $keyVaultName -ErrorAction SilentlyContinue
        
        if (-not $keyVault) {
            Write-Output "Creating Key Vault for encryption..."
            New-AzKeyVault -ResourceGroupName "${resourceGroupName}" -VaultName $keyVaultName -Location "${location}" -EnabledForDiskEncryption
        }
        
        # Enable disk encryption
        Set-AzVMDiskEncryptionExtension -ResourceGroupName "${resourceGroupName}" -VMName "${resourceName}" -DiskEncryptionKeyVaultUrl "https://$keyVaultName.vault.azure.net/" -DiskEncryptionKeyVaultId "/subscriptions/${subscriptionId}/resourceGroups/${resourceGroupName}/providers/Microsoft.KeyVault/vaults/$keyVaultName" -VolumeType All
        
        Write-Output "Disk encryption enabled successfully"
    }
    
} elseif ("${rec.title}".Contains("network") -or "${rec.title}".Contains("NSG")) {
    # Configure Network Security Group rules
    Write-Output "Configuring NSG security rules..."
    
    $nsgName = "${resourceName}"
    if ("${resourceType}" -ne "Microsoft.Network/networkSecurityGroups") {
        $nsgName = "${resourceName}-nsg"
    }
    
    $nsg = Get-AzNetworkSecurityGroup -ResourceGroupName "${resourceGroupName}" -Name $nsgName -ErrorAction SilentlyContinue
    
    if ($nsg) {
        # Add security rules
        Add-AzNetworkSecurityRuleConfig -NetworkSecurityGroup $nsg -Name "DenyDirectRDP" -Protocol "TCP" -SourcePortRange "*" -DestinationPortRange "3389" -SourceAddressPrefix "Internet" -DestinationAddressPrefix "*" -Access Deny -Priority 1000 -Direction Inbound
        Add-AzNetworkSecurityRuleConfig -NetworkSecurityGroup $nsg -Name "DenyDirectSSH" -Protocol "TCP" -SourcePortRange "*" -DestinationPortRange "22" -SourceAddressPrefix "Internet" -DestinationAddressPrefix "*" -Access Deny -Priority 1001 -Direction Inbound
        
        # Apply changes
        Set-AzNetworkSecurityGroup -NetworkSecurityGroup $nsg
        Write-Output "NSG security rules configured successfully"
    }
    
} elseif ("${rec.title}".Contains("Key Vault") -or "${rec.title}".Contains("vault")) {
    # Configure Key Vault security
    Write-Output "Configuring Key Vault security..."
    
    # Enable firewall and disable public access
    Update-AzKeyVault -ResourceGroupName "${resourceGroupName}" -VaultName "${resourceName}" -PublicNetworkAccess Disabled
    
    # Enable audit logging
    $storageAccountName = "staudit${resourceGroupName}".Replace('-', '').ToLower()
    $storageAccount = Get-AzStorageAccount -ResourceGroupName "${resourceGroupName}" -Name $storageAccountName -ErrorAction SilentlyContinue
    
    if (-not $storageAccount) {
        $storageAccount = New-AzStorageAccount -ResourceGroupName "${resourceGroupName}" -Name $storageAccountName -Location "${location}" -SkuName "Standard_LRS"
    }
    
    # Enable diagnostic settings
    $keyVault = Get-AzKeyVault -ResourceGroupName "${resourceGroupName}" -VaultName "${resourceName}"
    Set-AzDiagnosticSetting -ResourceId $keyVault.ResourceId -StorageAccountId $storageAccount.Id -Enabled $true -Category AuditEvent
    
    Write-Output "Key Vault security configured successfully"
}

Write-Output "Security hardening script completed successfully"`;
    }

    return `# Azure Resource Optimization Script
# Generated for: ${rec.title}
# Resource: ${resourceName}
# Resource Group: ${resourceGroupName}
# Subscription: ${subscriptionId}
# Category: ${rec.category}

# Connect to Azure
Connect-AzAccount

# Set subscription context
Set-AzContext -SubscriptionId "${subscriptionId}"

# Impacted Resources:
${impactedResources.map(res => `# - ${res.name} (${res.type}) in ${res.resourceGroup}`).join('\n')}

# Apply general optimization
$resource = Get-AzResource -ResourceId "/subscriptions/${subscriptionId}/resourceGroups/${resourceGroupName}/providers/${resourceType}/${resourceName}"
Write-Output "Processing resource: $($resource.Name)"

# Apply category-specific optimizations
switch ("${rec.category}") {
    "OperationalExcellence" {
        Write-Output "Applying operational excellence practices..."
        
        # Enable monitoring and diagnostics
        if ("${resourceType}" -eq "Microsoft.Compute/virtualMachines") {
            # Install Azure Monitor Agent
            Set-AzVMExtension -ResourceGroupName "${resourceGroupName}" -VMName "${resourceName}" -Name "AzureMonitorWindowsAgent" -Publisher "Microsoft.Azure.Monitor" -Type "AzureMonitorWindowsAgent" -TypeHandlerVersion "1.0" -Location "${location}"
        }
        
        Write-Output "Operational excellence practices applied"
    }
    "Performance" {
        Write-Output "Applying performance optimizations..."
        # Add performance-specific configurations
        Write-Output "Performance optimizations applied"
    }
    "Reliability" {
        Write-Output "Applying reliability improvements..."
        # Add reliability-specific configurations
        Write-Output "Reliability improvements applied"
    }
}

Write-Output "Resource optimization completed successfully"`;
  };

  const getPrerequisites = (rec: Recommendation): string[] => {
    const isGcp = (rec as any).provider === 'gcp';
    const common = isGcp
      ? ['gcloud CLI installed', 'Appropriate Google Cloud IAM permissions']
      : ['Azure PowerShell module installed', 'Appropriate Azure permissions'];
    if (rec.category === 'Security') {
      return isGcp
        ? [...common, 'Security Admin role', 'Org policy administrator access']
        : [...common, 'Security administrator role', 'Key Vault access'];
    }
    if (rec.category === 'Cost') {
      return isGcp
        ? [...common, 'Billing account permissions']
        : [...common, 'Cost management permissions'];
    }
    return common;
  };

  const getRollbackPlan = (rec: Recommendation): string => {
    if (rec.category === 'Cost') {
      return 'VM can be resized back to original size. Storage tier can be changed back to Hot. All changes are reversible with minimal impact.';
    }
    if (rec.category === 'Security') {
      return (rec as any).provider === 'gcp'
        ? 'Security configurations can be reverted using Google Cloud Console or gcloud. Backup configurations are automatically created before changes.'
        : 'Security configurations can be reverted using Azure Portal or PowerShell. Backup configurations are automatically created before changes.';
    }
    return (rec as any).provider === 'gcp'
      ? 'Configuration changes can be reverted through Google Cloud Console or API calls. Original settings are backed up automatically.'
      : 'Configuration changes can be reverted through Azure Portal or API calls. Original settings are backed up automatically.';
  };

  useEffect(() => {
    // Load data when provider changes
    loadCloudData();
    loadExecutiveSummary();
  }, [selectedProvider]);

  const fetchFreshData = async () => {
    setFetchingData(true);
    setFetchError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(buildApiUrl('/cloud/fetch-data'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch data');
      }

      if (result.success) {
        // After successful data fetch, reload the dashboard and summary
        await loadCloudData();
        await loadExecutiveSummary();
        
        // Show success message with details
        const stats = result.results;
        setError(null);
        // You could show a success notification here
        console.log(`Data fetch completed: ${stats.subscriptions_processed} subscriptions, ${stats.recommendations_collected} recommendations, ${stats.embeddings_created} embeddings`);
      } else {
        throw new Error(result.message || 'Data fetch failed');
      }
    } catch (error) {
      console.error('Error fetching fresh data:', error);
      setFetchError(error instanceof Error ? error.message : 'Failed to fetch data');
    } finally {
      setFetchingData(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Starting regular data refresh...');
      
      // Call the dedicated refresh endpoint
      const response = await fetch(buildApiUrl('/cloud/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Failed to refresh data');
      }

      console.log('Refresh completed:', result);

      // Reload dashboard data after successful refresh
      await loadCloudData();
      await loadExecutiveSummary();
      
      // Show success message briefly
      setError(null);
      
    } catch (error) {
      console.error('Error during refresh:', error);
      setError(error instanceof Error ? error.message : 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupAndRefresh = async () => {
    setFetchingData(true);
    setError(null);
    setFetchError(null);

    try {
      console.log('Starting complete cleanup and refresh process...');
      
      const response = await fetch(buildApiUrl('/cloud/cleanup-and-refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...((window as any).currentUser?.accessToken && {
            'Authorization': `Bearer ${(window as any).currentUser.accessToken}`
          })
        }
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Failed to cleanup and refresh data');
      }

      console.log('Cleanup and refresh completed:', result);
      
      // Reload dashboard data after successful refresh
      await loadCloudData();
      await loadExecutiveSummary();
      
      // Show success message
      setFetchError(null);
      
    } catch (error) {
      console.error('Error during cleanup and refresh:', error);
      setFetchError(error instanceof Error ? error.message : 'Failed to cleanup and refresh data');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatMessage.trim()) return;
    
    setChatLoading(true);
    const userMessage = chatMessage;
    setChatMessage('');
    
    // Add user message to history
  const userEntry: ChatMessage = { role: 'user', content: userMessage };
  const newHistory: ChatMessage[] = [...chatHistory, userEntry];
  setChatHistory(newHistory);
    
    try {
      // Check if this is a cloud infrastructure query
      const isCloudQuery = cloudManagementRAGService.isCloudInfrastructureQuery(userMessage);
      
      if (isCloudQuery) {
        // Use advanced cloud insights for infrastructure-related queries
        try {
          const insights = await cloudManagementRAGService.getAdvancedInsights(userMessage);
          
          let response = `## ${insights.query_type.replace('_', ' ').toUpperCase()} Analysis\n\n`;
          response += `${insights.ai_response}\n\n`;
          
          if (insights.insights.length > 0) {
            response += `### 📊 Key Insights:\n`;
            insights.insights.forEach((insight, index) => {
              response += `${index + 1}. ${insight}\n`;
            });
            response += '\n';
          }
          
          response += `*Confidence: ${cloudManagementRAGService.formatConfidence(insights.confidence_score)} | Data sources: ${insights.data_sources.join(', ')}*`;
          
          const assistantEntry: ChatMessage = { role: 'assistant', content: response };
          setChatHistory([...newHistory, assistantEntry]);
        } catch (cloudError) {
          console.warn('Cloud RAG analysis failed, falling back to standard chat:', cloudError);
          // Fallback to standard cloud chat
          await standardCloudChat(userMessage, newHistory);
        }
      } else {
        // Use standard cloud chat for non-infrastructure queries
        await standardCloudChat(userMessage, newHistory);
      }
    } catch (error) {
  const assistantError: ChatMessage = { role: 'assistant', content: 'Error processing your request. Please try again.' };
  setChatHistory([...newHistory, assistantError]);
    } finally {
      setChatLoading(false);
    }
  };

  const standardCloudChat = async (userMessage: string, newHistory: ChatMessage[]) => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }
    
  const response = await fetch(buildApiUrl('/cloud/chat'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: userMessage,
    subscriptionId: null,
    provider: selectedProvider === 'all' ? null : selectedProvider
      }),
    });

    const data = await response.json();
    if (data.success) {
      const assistantEntry: ChatMessage = { role: 'assistant', content: data.response, sources: data.documents };
      setChatHistory([...newHistory, assistantEntry]);
    } else {
      const assistantError: ChatMessage = { role: 'assistant', content: `Error: ${data.error || 'Sorry, I could not process your request.'}` };
      setChatHistory([...newHistory, assistantError]);
    }
  };

  // Use the aggregated data directly from the backend instead of calculating from recommendations
  const recommendations = cloudData?.recommendations || [];
  
  // Use backend-provided impact summary
  const highImpactCount = cloudData?.impactSummary?.High || 0;
  const mediumImpactCount = cloudData?.impactSummary?.Medium || 0;
  const lowImpactCount = cloudData?.impactSummary?.Low || 0;
  
  // Use the potentialSavings directly from the backend data instead of calculating from recommendations
  const totalPotentialSavings = cloudData?.potentialSavings || 0;

  // Prepare chart data
  const impactData = [
    { name: 'High Impact', value: highImpactCount, color: '#e74c3c' },
    { name: 'Medium Impact', value: mediumImpactCount, color: '#f39c12' },
    { name: 'Low Impact', value: lowImpactCount, color: '#2ecc71' }
  ];

  // Use backend-provided category summary
  const categoryData = cloudData?.categorySummary || {};
  const categoryChartData = Object.entries(categoryData).map(([name, value]) => ({
    name,
    value,
    color: `hsl(${Math.random() * 360}, 70%, 50%)`
  }));

  // Format Executive Summary markdown for clearer spacing between sections
  const formatExecutiveSummary = (md: string): string => {
    if (!md) return md;
    let s = md;
  // Remove any CLI/PowerShell/script code blocks and lines (no scripts in summary)
  s = stripScriptsFromMarkdown(s);
  // Ensure blank line before markdown headings
  s = s.replace(/\n?(## [^\n]+)/g, '\n\n$1');
  s = s.replace(/\n?(### [^\n]+)/g, '\n\n$1');
  s = s.replace(/\n?(#### [^\n]+)/g, '\n\n$1');
  // Ensure blank line after markdown headings
  s = s.replace(/(## [^\n]+)\n(?!\n)/g, '$1\n\n');
  s = s.replace(/(### [^\n]+)\n(?!\n)/g, '$1\n\n');
  s = s.replace(/(#### [^\n]+)\n(?!\n)/g, '$1\n\n');

  // Force breaks before emoji/titled sections even if not markdown headings
  s = s.replace(/\s*(📊\s*Executive Summary:[^\n]*)/gi, '\n\n$1');
  // Keep BUSINESS IMPACT ASSESSMENT as paragraph; just ensure spacing (no heading promotion)
  s = s.replace(/\s*(🎯\s*BUSINESS IMPACT ASSESSMENT)/gi, '\n\n$1');
  s = s.replace(/\s*(📋\s*STRATEGIC MITIGATION PLAN)/gi, '\n\n$1');

  // Join cases where 🎯 is on its own line and the label is on the next line
  s = s.replace(/(^|\n)\s*🎯\s*(\n+)\s*(BUSINESS IMPACT ASSESSMENT)/gi, '$1🎯 $3');
  // Or where the label appears first and 🎯 is on the previous line
  s = s.replace(/(^|\n)\s*🎯\s*$\n\s*(BUSINESS IMPACT ASSESSMENT)/gim, '$1🎯 $2');
  s = s.replace(/\s*(Phase\s*\d+\s*:\s*[^\n]+)/gi, '\n\n$1');
  s = s.replace(/\s*([🔴🟡🟢]\s*Task Group[^\n]+)/g, '\n\n$1');
  // Business Impact label (with or without markdown bold)**
  s = s.replace(/\s*((?:\*\*)?\s*Business\s*Impact\s*:?(?:\*\*)?)/gi, '\n\n$1');

  // Ensure an extra break after Executive Summary heading line
  s = s.replace(/(^|\n)(📊\s*Executive Summary:[^\n]*)(?=\n)/i, '$1$2\n');

  // Promote key tokens to headings so they render as titles
  const lines = s.split('\n');
  // Join a standalone emoji line with the following BUSINESS IMPACT ASSESSMENT label
  for (let i = 0; i < lines.length; i++) {
    const curr = lines[i].trim();
    if (!curr) continue;
    if (/^🎯\s*$/.test(curr)) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length) {
        let next = lines[j].trim();
        // strip heading marks and bold wrappers
        next = next.replace(/^#{1,6}\s*/, '');
        next = next.replace(/^\*\*(.*?)\*\*\s*$/, '$1');
        if (/^BUSINESS\s+IMPACT\s+ASSESSMENT/i.test(next)) {
          // preserve optional trailing colon
          const hasColon = /:\s*$/.test(next);
          const base = next.replace(/:\s*$/,'');
          lines[i] = `🎯 ${base}${hasColon ? ':' : ''}`;
          lines[j] = '';
        }
      }
    }
  }
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) continue;
    const isHeading = /^#+\s/.test(raw);
    if (!isHeading) {
      if (/^📊\s*Executive Summary:/i.test(line)) {
        lines[i] = `## ${raw.trim()}`;
        continue;
      }
      // Promote BUSINESS IMPACT ASSESSMENT to h3 so it matches 'Security Impact' size
      if (/^🎯\s*BUSINESS IMPACT ASSESSMENT/i.test(line) || /^BUSINESS IMPACT ASSESSMENT/i.test(line)) {
        lines[i] = `### ${raw.trim()}`;
        continue;
      }
      if (/^📋\s*STRATEGIC MITIGATION PLAN/i.test(line)) {
        lines[i] = `## ${raw.trim()}`;
        continue;
      }
      if (/^(Security Impact|Cost Impact|Operations Impact)\b/i.test(line)) {
        lines[i] = `### ${raw.trim()}`;
        continue;
      }
      if (/^Phase\s*\d+\s*:/i.test(line)) {
        lines[i] = `### ${raw.trim()}`;
        continue;
      }
      if (/^[🔴🟡🟢]\s*Task Group/i.test(line)) {
        lines[i] = `#### ${raw.trim()}`;
        continue;
      }
    }
  }
  s = lines.join('\n');
  // Remove empty heading stubs (##, ###, #### with no content)
  s = s.replace(/^\s*#{2,4}\s*$/gm, '');

    // Collapse runs of >2 newlines to exactly 2
    s = s.replace(/\n{3,}/g, '\n\n');
    return s;
  };

  // Remove CLI/PowerShell scripts and fenced code blocks from markdown
  const stripScriptsFromMarkdown = (md: string): string => {
    if (!md) return md;
    let s = md;
    // Remove fenced code blocks
    s = s.replace(/```[\s\S]*?```/g, '');
    // Remove indented code blocks (4 spaces or a tab)
    s = s.replace(/^(?: {4}|\t).*$\n?/gm, '');
    // Remove common CLI lines (az, gcloud, kubectl, bq, gsutil, pwsh, powershell, Set-Az*, Get-Az*, New-Az*, Remove-Az*)
    s = s.replace(/^\s*(?:\$\s*)?(?:az\b|gcloud\b|kubectl\b|bq\b|gsutil\b|pwsh\b|powershell\b|Set-Az\w*\b|Get-Az\w*\b|New-Az\w*\b|Remove-Az\w*\b).*$/gmi, '');
    // Collapse leftover excessive blank lines
    s = s.replace(/\n{3,}/g, '\n\n');
    return s.trim();
  };

  // Build a comprehensive posture summary from all recommendations
  const buildComprehensiveSummary = (recs: Recommendation[], providerFilter: 'all'|'azure'|'gcp'): string => {
    if (!recs || recs.length === 0) return 'No recommendations available.';

    // Filter by provider if needed
    const filtered = recs.filter(r => {
      const p = getProvider(r);
      return providerFilter === 'all' || p === providerFilter;
    });
    if (filtered.length === 0) return 'No recommendations available for the selected provider.';

    // Aggregations
    type Sev = 'High'|'Medium'|'Low';
    const sevOrder: Sev[] = ['High','Medium','Low'];
    const byProvider: Record<string, { total: number; sev: Record<Sev, number>; byCategory: Record<string, Record<Sev, number>> }>
      = {} as any;
    const byCategoryGlobal: Record<string, { total: number; sev: Record<Sev, number> }> = {} as any;

    const actionRegex = /^(Enable|Disable|Configure|Update|Upgrade|Rotate|Restrict|Set|Delete|Remove|Resize|Migrate|Apply|Add|Assign|Grant|Encrypt|Backup|Patch|Repair)\b/i;
    type GroupKey = string;
    const actionGroups: Record<GroupKey, {
      provider: 'azure'|'gcp';
      category: string;
      action: string;
      severity: Sev;
      resourceTypes: Record<string, number>;
      titles: string[];
      count: number;
      impactedResources: number;
    }> = {};

    const safe = (s?: string) => (s || '').toString();

    for (const r of filtered) {
      const provider = getProvider(r);
      const cat = safe(r.category) || 'General';
      const sev = (normalizeImpact(r) as Sev);
      const resType = safe(r.resourceType) || 'Resource';
      const title = safe(r.title);
      const m = title.match(actionRegex);
      const action = (m ? m[1] : 'Address');

      // byProvider
      if (!byProvider[provider]) {
        byProvider[provider] = { total: 0, sev: { High:0, Medium:0, Low:0 }, byCategory: {} };
      }
      byProvider[provider].total += 1;
      byProvider[provider].sev[sev] += 1;
      byProvider[provider].byCategory[cat] = byProvider[provider].byCategory[cat] || { High:0, Medium:0, Low:0 } as any;
      byProvider[provider].byCategory[cat][sev] += 1;

      // byCategoryGlobal
      if (!byCategoryGlobal[cat]) byCategoryGlobal[cat] = { total: 0, sev: { High:0, Medium:0, Low:0 } };
      byCategoryGlobal[cat].total += 1;
      byCategoryGlobal[cat].sev[sev] += 1;

      // actionGroups
      const key = `${provider}|${cat}|${action}|${sev}`;
      if (!actionGroups[key]) {
        actionGroups[key] = { provider, category: cat, action, severity: sev, resourceTypes: {}, titles: [], count: 0, impactedResources: 0 };
      }
      actionGroups[key].count += 1;
      actionGroups[key].resourceTypes[resType] = (actionGroups[key].resourceTypes[resType] || 0) + 1;
      if (title && actionGroups[key].titles.length < 5) actionGroups[key].titles.push(title);
      if (typeof r.impactedResources === 'number') actionGroups[key].impactedResources += (r.impactedResources || 0);
    }

    // Compose markdown
    const lines: string[] = [];
    lines.push('## 📊 Comprehensive Cloud Posture Summary');
    lines.push('');

    // Overview
    lines.push('### Posture Overview');
    const providers = Object.keys(byProvider);
    for (const p of providers) {
      const agg = byProvider[p];
      lines.push(`- ${p === 'azure' ? 'Azure' : 'Google Cloud'}: ${agg.total} issues (High ${agg.sev.High}, Medium ${agg.sev.Medium}, Low ${agg.sev.Low})`);
    }
    lines.push('');

    // Category breakdown
    lines.push('### Category Breakdown');
    const catEntries = Object.entries(byCategoryGlobal).sort((a,b)=> b[1].total - a[1].total);
    for (const [cat, info] of catEntries) {
      lines.push(`- ${cat}: ${info.total} (High ${info.sev.High}, Medium ${info.sev.Medium}, Low ${info.sev.Low})`);
    }
    lines.push('');

    // Key risk areas (top High by category)
    lines.push('### Key Risk Areas');
    const topRisk = catEntries
      .map(([cat, info]) => ({ cat, high: info.sev.High, total: info.total }))
      .filter(x => x.total > 0)
      .sort((a,b)=> b.high - a.high || b.total - a.total)
      .slice(0, 5);
    if (topRisk.length === 0) {
      lines.push('- No high-risk categories identified.');
    } else {
      for (const t of topRisk) lines.push(`- ${t.cat}: ${t.high} high-severity items`);
    }
    lines.push('');

    // Actionable mitigation plan grouped by category -> action -> severity
    lines.push('## 📋 Actionable Mitigation Plan');
    const groups = Object.values(actionGroups)
      .sort((a,b)=> sevOrder.indexOf(a.severity) - sevOrder.indexOf(b.severity) || b.count - a.count);
    const byCat: Record<string, typeof groups> = {} as any;
    for (const g of groups) {
      byCat[g.category] = byCat[g.category] || [];
      byCat[g.category].push(g);
    }
    for (const [cat, arr] of Object.entries(byCat)) {
      lines.push(`### ${cat}`);
      for (const g of arr) {
        const providerLabel = g.provider === 'azure' ? 'Azure' : 'Google Cloud';
        const typeSample = Object.entries(g.resourceTypes).sort((a,b)=> b[1]-a[1]).slice(0,2).map(([k])=>k).join(', ');
        const examples = g.titles.slice(0,3).join('; ');
        const impactText = g.impactedResources > 0 ? `; ~${g.impactedResources} resources impacted` : '';
        lines.push(`- ${g.severity} • ${providerLabel} • ${g.action} (${g.count} items${impactText})`);
        if (typeSample) lines.push(`  - Resource types: ${typeSample}`);
        if (examples) lines.push(`  - Examples: ${examples}`);
        // High-level steps (no CLI)
        lines.push(`  - Steps:`);
        lines.push(`    1. Identify affected resources in ${providerLabel} Console/Advisor.`);
        lines.push(`    2. Review impact and schedule a change window if needed.`);
        lines.push(`    3. Apply the configuration in the console or via policy/guardrails.`);
        lines.push(`    4. Verify remediation and monitor for regressions.`);
      }
      lines.push('');
    }

    // Quick wins section (heuristic)
    const quickWins = filtered.filter(r => {
      const t = safe(r.title).toLowerCase();
      return (r.quickFix === true) || (safe(r.complexity).toLowerCase() === 'low') || /\benable\b|\bturn on\b|\bconfigure\b/.test(t);
    });
    if (quickWins.length > 0) {
      lines.push('### Quick Wins');
      lines.push(`- ~${quickWins.length} fast-to-execute items across categories.`);
      const sample = quickWins.slice(0,5).map(r => `  - ${getProvider(r) === 'azure' ? 'Azure' : 'Google Cloud'}: ${safe(r.title)}`).join('\n');
      if (sample) lines.push(sample);
    }

    return lines.join('\n');
  };

  // Multi-cloud provider breakdowns (computed from current recommendation set)
  const getProvider = (rec: Recommendation): 'azure' | 'gcp' => {
    const p = ((rec as any).provider || '').toString().toLowerCase();
    if (p === 'gcp') return 'gcp';
    // Infer Azure if not marked as GCP
    const src = ((rec as any).resourceMetadata?.source || '').toString().toLowerCase();
    return src === 'gcp_recommender' ? 'gcp' : 'azure';
  };

  const providerAgg = recommendations.reduce((acc: any, rec) => {
    const prov = getProvider(rec);
    if (!acc[prov]) acc[prov] = { total: 0, High: 0, Medium: 0, Low: 0, savings: 0 };
    acc[prov].total += 1;
    const sev = normalizeImpact(rec);
    acc[prov][sev] += 1;
    const rawSave = (rec as any).extendedProperties?.savingsAmount;
    let save = 0;
    if (typeof rawSave === 'number') save = rawSave;
    else if (typeof rawSave === 'string') {
      const n = parseFloat(rawSave.replace(/[$,]/g, ''));
      if (!Number.isNaN(n)) save = n;
    }
    acc[prov].savings += save;
    return acc;
  }, {} as Record<string, { total: number; High: number; Medium: number; Low: number; savings: number }>);

  const providerTotalsChartData = [
    { name: 'Azure', value: (providerAgg.azure?.total || 0), color: '#0078d4' },
    { name: 'Google Cloud', value: (providerAgg.gcp?.total || 0), color: '#34a853' }
  ];

  const severityByProviderChartData = [
    {
      provider: 'Azure',
      High: providerAgg.azure?.High || 0,
      Medium: providerAgg.azure?.Medium || 0,
      Low: providerAgg.azure?.Low || 0,
    },
    {
      provider: 'Google Cloud',
      High: providerAgg.gcp?.High || 0,
      Medium: providerAgg.gcp?.Medium || 0,
      Low: providerAgg.gcp?.Low || 0,
    }
  ];

  const savingsByProviderData = [
    { provider: 'Azure', Savings: Math.round(providerAgg.azure?.savings || 0) },
    { provider: 'Google Cloud', Savings: Math.round(providerAgg.gcp?.savings || 0) }
  ];

  const healthScore = cloudData?.healthScore || 
    (recommendations.length === 0 ? 100 : 
     Math.max(0, 100 - (highImpactCount * 30 + mediumImpactCount * 15 + lowImpactCount * 5)));

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Spinner size={SpinnerSize.large} label="Loading cloud data..." />
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      minHeight: '100vh',
      padding: '32px',
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif'
    }} className={className}>
      <Stack tokens={{ childrenGap: 32 }}>
        {/* Modern Glassy Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '32px',
          padding: '32px',
          marginBottom: '0px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
        }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack>
              <Text variant="xxLarge" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: '700',
                marginBottom: '8px'
              }}>
                ☁️ Cloud Management Dashboard
              </Text>
              <Text variant="large" style={{ color: '#64748b', fontWeight: '500' }}>
                Cloud resource monitoring and AI-powered optimization
              </Text>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <Dropdown
                label="Provider"
                selectedKey={selectedProvider}
                onChange={(_, option) => setSelectedProvider((option?.key as 'all' | 'azure' | 'gcp') || 'all')}
                options={[
                  { key: 'all', text: 'All Providers' },
                  { key: 'azure', text: 'Azure' },
                  { key: 'gcp', text: 'Google Cloud' }
                ]}
                styles={{ dropdown: { minWidth: 180 } }}
              />
              <PrimaryButton 
                iconProps={{ iconName: fetchingData ? 'CloudAdd' : loading ? 'Sync' : 'Refresh' }}
                onClick={handleRefresh}
                text={fetchingData ? "Fetching Fresh Data..." : loading ? "Loading..." : "Refresh Data"}
                disabled={loading || fetchingData}
                style={{
                  background: fetchingData ? 'rgba(46, 204, 113, 0.1)' : 'rgba(102, 126, 234, 0.1)',
                  border: fetchingData ? '1px solid rgba(46, 204, 113, 0.3)' : '1px solid rgba(102, 126, 234, 0.3)',
                  color: fetchingData ? '#2ecc71' : '#667eea'
                }}
              />
              <DefaultButton 
                iconProps={{ iconName: fetchingData ? 'Delete' : 'RecycleBin' }}
                onClick={handleCleanupAndRefresh}
                text={fetchingData ? "Cleaning & Refreshing..." : "Clean & Refresh"}
                disabled={loading || fetchingData}
                style={{
                  background: 'rgba(231, 76, 60, 0.1)',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  color: '#e74c3c'
                }}
                title="Remove all existing data and fetch fresh data from Azure Advisor"
              />
            </Stack>
          </Stack>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error}>
            {error}
          </MessageBar>
        )}

        {fetchingData && (
          <MessageBar messageBarType={MessageBarType.info}>
            🔄 Fetching fresh data from Azure Advisor... This includes collecting recommendations, generating embeddings, and creating an AI-powered executive summary. This may take a few minutes.
          </MessageBar>
        )}

        {fetchError && (
          <MessageBar messageBarType={MessageBarType.error}>
            Failed to fetch fresh data: {fetchError}
          </MessageBar>
        )}

        <Pivot selectedKey={selectedPivot} onLinkClick={(item) => setSelectedPivot(item?.props.itemKey || 'dashboard')}>
          <PivotItem headerText="Dashboard Overview" itemKey="dashboard" itemIcon="ViewDashboard">
            <Stack tokens={{ childrenGap: 32 }} style={{ marginTop: '24px' }}>
              {/* Metrics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px'
              }}>
                <MetricCard
                  title="Cloud Health Score"
                  value={`${healthScore.toFixed(1)}%`}
                  subtitle="Overall cloud optimization health"
                  icon="Health"
                  color="#2ecc71"
                  trend={5.2}
                />
                <MetricCard
                  title="Total Accounts/Projects"
                  value={cloudData?.totalSubscriptions || 0}
                  subtitle="Active cloud accounts/projects"
                  icon="Cloud"
                  color="#0078d4"
                />
                <MetricCard
                  title="Active Recommendations"
                  value={cloudData?.totalRecommendations || 0}
                  subtitle="Optimization opportunities"
                  icon="Lightbulb"
                  color="#f39c12"
                  trend={-8.1}
                />
                <MetricCard
                  title="Potential Savings"
                  value={`$${totalPotentialSavings.toLocaleString()}`}
                  subtitle="Monthly cost reduction"
                  icon="Money"
                  color="#9b59b6"
                  trend={12.3}
                />
              </div>

              {/* Charts Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
                gap: '32px'
              }}>
                <ChartWidget title="Recommendations by Impact" icon="Warning">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={impactData}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {impactData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartWidget>

                <ChartWidget title="Recommendations by Category" icon="Chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="name" 
                        stroke="#4a5568"
                        fontSize={12}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis stroke="#4a5568" fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#667eea" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWidget>
              </div>

              {/* Multi-cloud provider widgets */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
                gap: '32px'
              }}>
                <ChartWidget title="Issues by Cloud Provider" icon="Cloud">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={providerTotalsChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {providerTotalsChartData.map((entry, index) => (
                          <Cell key={`prov-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartWidget>

                <ChartWidget title="Severity by Provider" icon="Warning">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={severityByProviderChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="provider" stroke="#4a5568" fontSize={12} />
                      <YAxis stroke="#4a5568" fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="High" stackId="a" fill="#e74c3c" />
                      <Bar dataKey="Medium" stackId="a" fill="#f39c12" />
                      <Bar dataKey="Low" stackId="a" fill="#2ecc71" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWidget>
              </div>

              <ChartWidget title="Potential Savings by Provider" icon="Money" height="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={savingsByProviderData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="provider" stroke="#4a5568" fontSize={12} />
                    <YAxis stroke="#4a5568" fontSize={12} />
                    <Tooltip formatter={(v: any) => [`$${Number(v).toLocaleString()}`, 'Savings']} />
                    <Bar dataKey="Savings" fill="#9b59b6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartWidget>

              {/* Health Timeline */}
              <ChartWidget title="Cloud Health Timeline" icon="Timeline" height="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { date: '7 days ago', health: 82.1 },
                    { date: '6 days ago', health: 84.3 },
                    { date: '5 days ago', health: 78.9 },
                    { date: '4 days ago', health: 86.2 },
                    { date: '3 days ago', health: 88.7 },
                    { date: '2 days ago', health: 85.4 },
                    { date: 'Yesterday', health: 87.2 },
                    { date: 'Today', health: healthScore }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#4a5568"
                      fontSize={12}
                    />
                    <YAxis stroke="#4a5568" fontSize={12} domain={[70, 100]} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="health" 
                      stroke="#2ecc71" 
                      strokeWidth={3}
                      dot={{ fill: '#2ecc71', strokeWidth: 2, r: 6 }}
                      activeDot={{ r: 8, stroke: '#2ecc71', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartWidget>
            </Stack>
          </PivotItem>

          <PivotItem headerText="Executive Summary" itemKey="executive-summary" itemIcon="ReportDocument">
            <div style={{ marginTop: '24px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '24px',
                padding: '32px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    borderRadius: '16px',
                    padding: '12px',
                    marginRight: '16px'
                  }}>
                    <Icon iconName="ReportDocument" style={{ fontSize: '24px', color: '#ffffff' }} />
                  </div>
                  <Stack>
                    <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
                      Executive Summary
                    </Text>
                    <Text variant="medium" style={{ color: '#718096' }}>
                      AI-powered insights and strategic recommendations
                    </Text>
                  </Stack>
                </Stack>
                
                {summaryLoading ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '300px'
                  }}>
                    <Spinner size={SpinnerSize.large} label="Loading executive summary..." />
                  </div>
                ) : cloudData?.executiveSummary ? (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.6)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <style>{execSummaryStyles}</style>
                    <div className="exec-summary">
                      <ReactMarkdown 
                        components={{
                          h1: ({children}) => children && String(children).trim() ? <h1 className="exec-h1">{children}</h1> : <></>,
                          h2: ({children}) => children && String(children).trim() ? (
                            <h2 className={`exec-h2 ${String(children).startsWith('📊') ? 'exec-title' : ''}`}>{children}</h2>
                          ) : <></>,
                          h3: ({children}) => children && String(children).trim() ? <h3 className="exec-h3">{children}</h3> : <></>,
                          h4: ({children}) => children && String(children).trim() ? <h4 className="exec-h4">{children}</h4> : <></>,
                          p: ({children}) => children && String(children).trim() ? <p className="exec-p">{children}</p> : <></>,
                          ul: ({children}) => <ul>{children}</ul>,
                          ol: ({children}) => <ol>{children}</ol>,
                          li: ({children}) => <li>{children}</li>,
                          strong: ({children}) => <strong>{children}</strong>,
                          em: ({children}) => <em>{children}</em>,
                        }}
                      >
                        {formatExecutiveSummary(stripScriptsFromMarkdown(cloudData.executiveSummary))}
                      </ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '300px',
                    color: '#718096'
                  }}>
                    <Icon iconName="FileOctet" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                    <Text variant="large" style={{ marginBottom: '8px' }}>No Executive Summary Available</Text>
                    <Text>Click "Refresh Data" to generate an AI-powered executive summary</Text>
                  </div>
                )}
              </div>
            </div>
          </PivotItem>

          <PivotItem headerText="Comprehensive Summary" itemKey="comprehensive-summary" itemIcon="Board">
            <div style={{ marginTop: '24px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '24px',
                padding: '32px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              }}>
                <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    borderRadius: '16px',
                    padding: '12px',
                    marginRight: '16px'
                  }}>
                    <Icon iconName="Board" style={{ fontSize: '24px', color: '#ffffff' }} />
                  </div>
                  <Stack style={{ flex: 1 }}>
                    <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
                      Comprehensive Summary
                    </Text>
                    <Text variant="medium" style={{ color: '#718096' }}>
                      Full cloud posture with grouped, actionable mitigations (no scripts)
                    </Text>
                  </Stack>
                  <DefaultButton text={regenBusy ? 'Regenerating…' : 'Regenerate'} disabled={regenBusy}
                    onClick={handleRegenerateComprehensive} />
                </Stack>

                {loadingComprehensive ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Spinner size={SpinnerSize.large} label="Building summary..." />
                  </div>
                ) : (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.6)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <style>{execSummaryStyles}</style>
                    <div className="exec-summary">
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => children && String(children).trim() ? <h1 className="exec-h1">{children}</h1> : <></>,
                          h2: ({children}) => children && String(children).trim() ? <h2 className="exec-h2">{children}</h2> : <></>,
                          h3: ({children}) => children && String(children).trim() ? <h3 className="exec-h3">{children}</h3> : <></>,
                          h4: ({children}) => children && String(children).trim() ? <h4 className="exec-h4">{children}</h4> : <></>,
                          p: ({children}) => children && String(children).trim() ? <p className="exec-p">{children}</p> : <></>,
                          ul: ({children}) => <ul>{children}</ul>,
                          ol: ({children}) => <ol>{children}</ol>,
                          li: ({children}) => <li>{children}</li>,
                          strong: ({children}) => <strong>{children}</strong>,
                          em: ({children}) => <em>{children}</em>,
                        }}
                      >
                        {formatExecutiveSummary(comprehensiveSummary || buildComprehensiveSummary(recommendations, selectedProvider))}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </PivotItem>

          <PivotItem headerText="Recommendations" itemKey="recommendations" itemIcon="BulletedList">
            <div style={{ marginTop: '24px' }}>
              {loading ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px'
                }}>
                  <Spinner size={SpinnerSize.large} label="Loading recommendations..." />
                </div>
              ) : (
                <Stack tokens={{ childrenGap: 32 }}>
                  {/* Summary Stats */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '24px',
                    padding: '32px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  }}>
                    <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        borderRadius: '16px',
                        padding: '12px',
                        marginRight: '16px'
                      }}>
                        <Icon iconName="BulletedList" style={{ fontSize: '24px', color: '#ffffff' }} />
                      </div>
                      <Stack style={{ flex: 1 }}>
                        <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
                          Recommendations Overview
                        </Text>
                        <Text variant="medium" style={{ color: '#718096' }}>
                          {cloudData.recommendations.length} total recommendations
                        </Text>
                      </Stack>
                    </Stack>
                    
                    {/* Summary Stats Bar */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '20px'
                    }}>
                      <div style={{
                        background: 'rgba(231, 76, 60, 0.1)',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(231, 76, 60, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
                          <Text variant="xxLarge" style={{ fontWeight: '700', color: '#e74c3c' }} styles={{ root: { display: 'block' } }}>
                            {cloudData.recommendations.filter(r => normalizeImpact(r) === 'High').length}
                          </Text>
                          <Text variant="medium" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                            High Impact
                          </Text>
                        </Stack>
                      </div>
                      <div style={{
                        background: 'rgba(243, 156, 18, 0.1)',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(243, 156, 18, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
                          <Text variant="xxLarge" style={{ fontWeight: '700', color: '#f39c12' }} styles={{ root: { display: 'block' } }}>
                            {cloudData.recommendations.filter(r => normalizeImpact(r) === 'Medium').length}
                          </Text>
                          <Text variant="medium" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                            Medium Impact
                          </Text>
                        </Stack>
                      </div>
                      <div style={{
                        background: 'rgba(46, 204, 113, 0.1)',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(46, 204, 113, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
                          <Text variant="xxLarge" style={{ fontWeight: '700', color: '#2ecc71' }} styles={{ root: { display: 'block' } }}>
                            {cloudData.recommendations.filter(r => normalizeImpact(r) === 'Low').length}
                          </Text>
                          <Text variant="medium" style={{ color: '#2d3748' }} styles={{ root: { display: 'block' } }}>
                            Low Impact
                          </Text>
                        </Stack>
                      </div>
                      <div style={{
                        background: 'rgba(155, 89, 182, 0.1)',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(155, 89, 182, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Text variant="xxLarge" style={{ fontWeight: '700', color: '#9b59b6', marginBottom: '8px' }}>
                          {cloudData.recommendations.filter(r => r.quickFix).length}
                        </Text>
                        <Text variant="medium" style={{ color: '#2d3748' }}>
                          Quick Fixes
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Category Grid - Azure Advisor Style */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '24px'
                  }}>
                    {Object.entries(CATEGORIES).map(([categoryKey, categoryInfo]) => {
                      const stats = getCategoryStats(categoryKey);
                      if (stats.total === 0) return null;
                      
                      return (
                        <CategoryWidget
                          key={categoryKey}
                          title={categoryKey}
                          icon={categoryInfo.icon}
                          color={categoryInfo.color}
                          totalRecommendations={stats.total}
                          highImpact={stats.high}
                          mediumImpact={stats.medium}
                          lowImpact={stats.low}
                          description={categoryInfo.description}
                          onClick={() => handleCategoryClick(categoryKey)}
                        />
                      );
                    })}
                  </div>
                </Stack>
              )}
            </div>
          </PivotItem>
          <PivotItem headerText="AI Assistant" itemKey="chat" itemIcon="Chat">
            <div style={{ marginTop: '24px' }}>
              <ChartWidget title="Cloud Architecture Assistant - Multi-Cloud Expert" icon="Robot" height="700px">
                <Stack tokens={{ childrenGap: 16 }} style={{ height: '100%' }}>
                  
                  {/* Chat History */}

                  <div style={{
                    flex: 1,
                    overflow: 'auto',
                    background: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '12px',
                    padding: '16px',
                    minHeight: '420px'
                  }}>
                    {chatHistory.length === 0 ? (
                      <div style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{ width: '100%', maxWidth: '1100px' }}>
                          <div style={{
                            background: '#ffffff',
                            borderRadius: '28px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                            padding: '6px 8px',
                            width: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                              <TextField
                                placeholder={`Ask about cloud recommendations, request ${selectedProvider === 'gcp' ? 'gcloud' : 'PowerShell'} scripts, or optimization plans...`}
                                value={chatMessage}
                                onChange={(_, value) => setChatMessage(value || '')}
                                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSubmit()}
                                styles={{
                                  root: {
                                    flex: 1,
                                    minWidth: 0,
                                  },
                                  fieldGroup: {
                                    border: 'none',
                                    background: 'transparent',
                                    borderRadius: 24,
                                      boxShadow: 'none',
                                      width: '100%'
                                  },
                                  field: {
                                    fontSize: 16,
                                    padding: '10px 12px'
                                  }
                                }}
                              />
                              <IconButton
                                iconProps={{ iconName: 'Send' }}
                                ariaLabel="Send"
                                onClick={handleChatSubmit}
                                disabled={chatLoading || !chatMessage.trim()}
                                styles={{
                                  root: {
                                    background: '#0f6cbd',
                                    color: '#fff',
                                    borderRadius: 20,
                                    width: 40,
                                    height: 40,
                                    marginLeft: 'auto'
                                  },
                                  rootDisabled: { background: '#cbd5e1', color: '#fff' },
                                  icon: { fontSize: 16 }
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', marginTop: 10 }}>
                            <Text style={{ color: '#4a5568' }}>Answers are grounded in your cloud data with citations.</Text>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Stack tokens={{ childrenGap: 12 }}>
                        {chatHistory.map((message, index) => (
                          <div key={index} style={{ display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start' }}>
                            <div
                              style={{
                                padding: '12px',
                                borderRadius: '12px',
                                background: message.role === 'user'
                                  ? 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)'
                                  : '#ffffff',
                                color: '#1a202c',
                                maxWidth: '78%',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                              }}
                            >
                              <Text style={{ whiteSpace: 'pre-wrap' }}>{message.content}</Text>
                              {message.role === 'assistant' && message.sources && Array.isArray(message.sources) && message.sources.length > 0 && (
                                <div style={{ marginTop: '8px', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                                  <Text variant="small" style={{ color: '#4a5568', fontWeight: 600 }}>Sources ({message.sources.length}):</Text>
                                  <ul style={{ margin: '6px 0 0 16px', color: '#4a5568' }}>
                                    {message.sources.slice(0, 5).map((src: any, i: number) => (
                                      <li key={i}>
                                        <span style={{ fontWeight: 600 }}>{src.title || 'Cloud Recommendation'}</span>
                                        {src.source ? <span> — {src.source}</span> : null}
                                        {typeof src?.metadata?.similarity === 'number' ? <span> (sim {src.metadata.similarity.toFixed(2)})</span> : null}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {chatLoading && (
                          <div style={{
                            padding: '12px',
                            borderRadius: '8px',
                            background: '#ffffff',
                            alignSelf: 'flex-start'
                          }}>
                            <Spinner size={SpinnerSize.small} />
                          </div>
                        )}
                      </Stack>
                    )}
                  </div>

                  {/* Chat Input */}
                  {chatHistory.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '100%', maxWidth: '1100px' }}>
                        <div style={{
                          background: '#ffffff',
                          borderRadius: 24,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                          padding: 6,
                          width: '100%'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                            <TextField
                              placeholder={`Ask about cloud recommendations, request ${selectedProvider === 'gcp' ? 'gcloud' : 'PowerShell'} scripts, or optimization plans...`}
                              value={chatMessage}
                              onChange={(_, value) => setChatMessage(value || '')}
                              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSubmit()}
                              styles={{
                                root: {
                                  flex: 1,
                                  minWidth: 0,
                                },
                                fieldGroup: {
                                  border: 'none',
                                  background: 'transparent',
                                  borderRadius: 18,
                                  boxShadow: 'none',
                                  width: '100%'
                                },
                                field: { fontSize: 15, padding: '10px 12px' }
                              }}
                            />
                            <IconButton
                              iconProps={{ iconName: 'Send' }}
                              ariaLabel="Send"
                              onClick={handleChatSubmit}
                              disabled={chatLoading || !chatMessage.trim()}
                              styles={{
                                root: {
                                  background: '#0f6cbd',
                                  color: '#fff',
                                  borderRadius: 20,
                                  width: 40,
                                  height: 40,
                                  marginLeft: 'auto'
                                },
                                rootDisabled: { background: '#cbd5e1', color: '#fff' },
                                icon: { fontSize: 16 }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Stack>
              </ChartWidget>
            </div>
          </PivotItem>
        </Pivot>
      </Stack>
      
      {/* Category Details Panel */}
      <Panel
        isOpen={showCategoryDetails}
        onDismiss={() => setShowCategoryDetails(false)}
        type={PanelType.largeFixed}
        headerText={`${selectedCategory} Recommendations`}
        isLightDismiss={true}
      >
        {selectedCategory && (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="medium" style={{ color: '#4a5568', marginBottom: '16px' }}>
              Showing all {selectedCategory.toLowerCase()} recommendations with detailed fix scripts.
            </Text>
            
            {cloudData.recommendations
              .filter(rec => normalizeCategory(rec) === selectedCategory)
              .map(rec => (
                <div
                  key={rec.id}
                  style={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleRecommendationClick(rec)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Stack horizontal horizontalAlign="space-between" verticalAlign="start" style={{ marginBottom: '12px' }}>
                    <Stack style={{ flex: 1, marginRight: '16px' }}>
                      <Text variant="medium" style={{ fontWeight: '600', color: '#2d3748', marginBottom: '4px' }}>
                        {rec.title}
                      </Text>
                      <Text variant="small" style={{ color: '#4a5568', lineHeight: '1.5' }}>
                        {rec.description}
                      </Text>
                    </Stack>
                    
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                      {rec.quickFix && (
                        <div style={{
                          background: '#2ecc71',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          Quick Fix
                        </div>
                      )}
                      <div style={{
                        background: rec.impact === 'High' ? '#e74c3c' : rec.impact === 'Medium' ? '#f39c12' : '#2ecc71',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {rec.impact}
                      </div>
                      <IconButton
                        iconProps={{ iconName: 'Settings' }}
                        title="View Fix Details"
                        styles={{
                          root: {
                            color: '#667eea',
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            borderRadius: '50%'
                          }
                        }}
                      />
                    </Stack>
                  </Stack>
                  
                  <Stack horizontal tokens={{ childrenGap: 16 }} style={{ marginTop: '12px' }}>
                    <Text variant="xSmall" style={{ color: '#718096' }}>
                      <strong>Resource:</strong> {rec.resourceId?.split('/').pop() || 'N/A'}
                    </Text>
                    <Text variant="xSmall" style={{ color: '#718096' }}>
                      <strong>Downtime:</strong> {rec.estimatedDowntime}
                    </Text>
                    <Text variant="xSmall" style={{ color: '#718096' }}>
                      <strong>Complexity:</strong> {rec.complexity}
                    </Text>
                  </Stack>
                </div>
              ))}
          </Stack>
        )}
      </Panel>

      {/* Recommendation Detail Modal */}
      <RecommendationDetailModal
        recommendation={selectedRecommendation}
        isOpen={showFixDetails}
        onClose={() => setShowFixDetails(false)}
        onGenerateScript={generateMitigationScript}
        generatedScripts={generatedScripts}
        scriptGenerationLoading={scriptGenerationLoading}
      />

      {/* Mitigation Plan Panel */}
      <Panel
        isOpen={isMitigationPanelOpen}
        onDismiss={() => setIsMitigationPanelOpen(false)}
        type={PanelType.medium}
        headerText="Cloud Mitigation Action Plan"
        closeButtonAriaLabel="Close"
        styles={{
          main: { backgroundColor: '#fafafa' },
          header: { 
            backgroundColor: '#0078d4',
            color: 'white'
          },
          headerText: { color: 'white' }
        }}
      >
        {executiveSummary && (
          <Stack tokens={{ childrenGap: 20 }}>
            {/* Phase Selection */}
            <Stack>
              <Text variant="medium" style={{ fontWeight: '600', marginBottom: '8px' }}>
                Select Mitigation Phase:
              </Text>
              <Dropdown
                selectedKey={selectedMitigationPhase}
                onChange={(_, option) => setSelectedMitigationPhase(option?.key as string)}
                options={[
                  { key: 'phase-1', text: '🔥 Phase 1: Immediate (0-30 days)' },
                  { key: 'phase-2', text: '⚡ Phase 2: Short-term (30-90 days)' },
                  { key: 'phase-3', text: '🏗️ Phase 3: Long-term (90+ days)' }
                ]}
                styles={{
                  dropdown: { borderRadius: '8px' }
                }}
              />
            </Stack>

            {/* Mitigation Content */}
            <Stack>
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e1e5e9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <Text variant="mediumPlus" style={{ fontWeight: '600', marginBottom: '12px' }}>
                  {selectedMitigationPhase === 'phase-1' && '🔥 Immediate Actions (Critical Priority)'}
                  {selectedMitigationPhase === 'phase-2' && '⚡ Short-term Improvements'}
                  {selectedMitigationPhase === 'phase-3' && '🏗️ Long-term Strategic Changes'}
                </Text>
                
                {/* Sample mitigation content - this would come from the backend */}
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  {mitigationLoading ? (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                      <Spinner size={SpinnerSize.medium} />
                      <Text>Loading mitigation plan...</Text>
                    </Stack>
                  ) : mitigationData[selectedMitigationPhase] ? (
                    mitigationData[selectedMitigationPhase].error ? (
                      <MessageBar messageBarType={MessageBarType.error}>
                        {mitigationData[selectedMitigationPhase].error}
                      </MessageBar>
                    ) : (
                      <ReactMarkdown>
                        {mitigationData[selectedMitigationPhase].content}
                      </ReactMarkdown>
                    )
                  ) : (
                    <ReactMarkdown>
                      {selectedMitigationPhase === 'phase-1' ? `
### 🔥 Immediate Cost Optimization (0-30 days)

#### Task 1: Remove Unused Resources
**Business Impact**: Save $2,400/month
**Script**:
\`\`\`powershell
# Find and deallocate unused VMs
Get-AzVM | Where-Object {$_.PowerState -eq "PowerState/stopped"} | 
  Stop-AzVM -Force
\`\`\`

#### Task 2: Rightsize Oversized VMs
**Business Impact**: Save $1,800/month
**Script**:
\`\`\`azurecli
# Get VM utilization recommendations
az advisor recommendation list --category Cost --output table
\`\`\`

#### Task 3: Enable Reserved Instances
**Business Impact**: Save $3,200/month
- Purchase 1-year reserved instances for production VMs
- Implement auto-shutdown for dev/test environments
                      ` : selectedMitigationPhase === 'phase-2' ? `
### ⚡ Short-term Optimization (30-90 days)

#### Task 1: Implement Azure Policy
**Business Impact**: Prevent $5,000/month in waste
**Script**:
\`\`\`azurecli
# Create resource naming policy
az policy definition create --name "require-tags" --rules policy.json
\`\`\`

#### Task 2: Setup Cost Alerts
**Business Impact**: Early warning system
- Configure budget alerts at 80% threshold
- Implement department-level charge back

#### Task 3: Storage Optimization
**Business Impact**: Save $800/month
- Move infrequently accessed data to cool storage
- Implement lifecycle management policies
                      ` : `
### 🏗️ Long-term Strategic Changes (90+ days)

#### Task 1: Cloud Governance Framework
**Business Impact**: 15-25% overall cost reduction
- Establish cloud center of excellence
- Implement advanced monitoring and analytics

#### Task 2: Multi-cloud Strategy
**Business Impact**: Risk mitigation and cost optimization
- Evaluate hybrid cloud opportunities
- Implement disaster recovery optimization

#### Task 3: Automation & DevOps
**Business Impact**: 30% operational efficiency gain
- Infrastructure as Code implementation
- Automated scaling and optimization
                      `}
                    </ReactMarkdown>
                  )}
                </div>

                {/* AI Assistant for Real-time Suggestions */}
                <Separator style={{ margin: '20px 0' }} />
                
                <Stack>
                  <Text variant="medium" style={{ fontWeight: '600', marginBottom: '8px' }}>
                    💡 Get AI-Powered Script Generation
                  </Text>
                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <TextField
                      placeholder="Ask for specific scripts or customizations..."
                      style={{ flex: 1 }}
                      value={aiAssistantQuery}
                      onChange={(_, value) => setAiAssistantQuery(value || '')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !aiAssistantLoading) {
                          handleAiAssistantQuery();
                        }
                      }}
                    />
                    <PrimaryButton
                      text="Generate"
                      iconProps={{ iconName: 'Robot' }}
                      onClick={handleAiAssistantQuery}
                      disabled={aiAssistantLoading || !aiAssistantQuery.trim()}
                    />
                  </Stack>
                  
                  {aiAssistantLoading && (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginTop: '12px' }}>
                      <Spinner size={SpinnerSize.small} />
                      <Text variant="small">AI is generating response...</Text>
                    </Stack>
                  )}
                  
                  {aiMitigationResponse && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#e8f4fd',
                      borderRadius: '8px',
                      border: '1px solid #bee5eb'
                    }}>
                      <ReactMarkdown>{aiMitigationResponse}</ReactMarkdown>
                    </div>
                  )}
                </Stack>
              </div>
            </Stack>

            {/* Action Buttons */}
            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <PrimaryButton
                text="Download Scripts"
                iconProps={{ iconName: 'Download' }}
                onClick={() => {
                  // Handle script download
                  console.log('Downloading scripts for', selectedMitigationPhase);
                }}
              />
              <DefaultButton
                text="Export Plan"
                iconProps={{ iconName: 'Export' }}
                onClick={() => {
                  // Handle plan export
                  console.log('Exporting mitigation plan');
                }}
              />
            </Stack>
          </Stack>
        )}
      </Panel>
    </div>
  );
};

export default CloudDashboard;
